import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { Modal } from "react-bootstrap";
import { FaTimes, FaCheck, FaGripLinesVertical, FaSearchPlus, FaSearchMinus, FaCompress, FaArrowUp, FaArrowDown, FaTrash, FaVectorSquare } from "react-icons/fa";
import { Document, Page } from "react-pdf";
import { format } from "date-fns";
import useFetchData from "./useFetchDatav2";
import PayeePickerModal from "./PayeePickerModal";
import { useToast } from "../Context/useToast";
import "../apps/InvoiceStatements.css";
import "./PdfModal.css";

const HIGHLIGHT_COLORS = {
  supplier: "#0d6efd",
  statementDate: "#198754",
  amountDue: "#fd7e14",
  tableHeader: "#adb5bd",
  headerGroup: "#c4b5fd",
  invoiceHeaderActive: "#0d6efd",
  invoiceColumnBand: "#93c5fd",
};

// [ZONE MATCHING] Per-column color palette for zone stripe overlay
const ZONE_COLORS = {
  InvoiceLineDate:      "#10b981",
  InvoiceLineInvoiceID: "#3b82f6",
  InvoiceLinePoNo:      "#8b5cf6",
  InvoiceLineAmount:    "#f59e0b",
  default:              "#94a3b8",
};

const MANDATORY_STATEMENT_ANY_OF = ["SupplierParentName", "SupplierTradingName"];
const MANDATORY_STATEMENT_ALL = ["StatementDate"];
const MANDATORY_INVOICE_COLUMNS = ["InvoiceLineDate", "InvoiceLineInvoiceID", "InvoiceLineAmount"];

const STATEMENT_LABEL_DISPLAY = {
  SupplierParentName: "Supplier Parent Name",
  SupplierTradingName: "Franchise Name",
  StatementDate: "Statement Date",
};

const INVOICE_COLUMN_DISPLAY = {
  InvoiceLineDate: "Invoice Date",
  InvoiceLineInvoiceID: "Invoice ID",
  InvoiceLineAmount: "Invoice Amount",
};

const PUBLISH_DOCUMENT_ONBOARDING_ENDPOINT = "/publish_document_onboarding.sz";

const toBoolean = (value) => {
  if (typeof value === "boolean") return value;
  if (value == null) return false;
  const text = String(value).trim().toLowerCase();
  return text === "1" || text === "true" || text === "yes";
};

const uniqueStrings = (items = []) => {
  const values = items
    .map((item) => String(item || "").trim())
    .filter(Boolean);
  return [...new Set(values)];
};

const mergeBoxes = (boxes = []) => {
  if (!boxes.length) return null;

  const minX = Math.min(...boxes.map((b) => b.x));
  const minY = Math.min(...boxes.map((b) => b.y));
  const maxX2 = Math.max(...boxes.map((b) => b.x2));
  const maxY2 = Math.max(...boxes.map((b) => b.y2));

  return {
    x: minX,
    y: minY,
    x2: maxX2,
    y2: maxY2,
    width: maxX2 - minX,
    height: maxY2 - minY,
    page: boxes[0]?.page,
  };
};

/**
 * Parses a single-line Canadian address string into structured fields.
 * Handles the common format: "1147 DUNDAS STREET WOODSTOCK, ON N4S 8W3"
 */
function parseCanadianAddress(address) {
  if (!address) return null;

  const cleaned = String(address).trim().replace(/\s+/g, " ");

  // Primary: STREET CITY, PROVINCE POSTALCODE
  const match = cleaned.match(
    /^(.+?)\s+([^,]+),\s*([A-Z]{2})\s+([A-Z]\d[A-Z]\s?\d[A-Z]\d)$/i
  );
  if (match) {
    return {
      street:     match[1].trim(),
      city:       match[2].trim(),
      province:   match[3].trim().toUpperCase(),
      postal_code: match[4].trim().toUpperCase().replace(/\s/, " "),
      country:    "CA",
    };
  }

  // Fallback: extract what we can from the raw string
  const postalMatch   = cleaned.match(/\b([A-Z]\d[A-Z]\s?\d[A-Z]\d)\b/i);
  const provinceMatch = cleaned.match(/\b(AB|BC|MB|NB|NL|NS|NT|NU|ON|PE|QC|SK|YT)\b/i);
  const postal_code   = postalMatch  ? postalMatch[1].toUpperCase().replace(/\s/, " ") : "";
  const province      = provinceMatch ? provinceMatch[1].toUpperCase() : "";

  let remainder = cleaned;
  if (postal_code)  remainder = remainder.replace(postalMatch[0], "");
  if (province)     remainder = remainder.replace(provinceMatch[0], "");
  remainder = remainder.replace(/[,]+/g, " ").replace(/\s+/g, " ").trim();

  return {
    street:     remainder,
    city:       "",
    province,
    postal_code,
    country:    postal_code ? "CA" : "",
  };
}

/** Normalise a country string to a 2-letter ISO code. */
function normalizeCountry(value) {
  const v = String(value || "").trim().toUpperCase();
  if (!v) return "";
  if (v === "CANADA" || v === "CAN")        return "CA";
  if (v === "UNITED STATES" || v === "USA" || v === "US") return "US";
  // Already a 2-letter code
  if (v.length === 2) return v;
  return value.trim();
}

// [ZONE MATCHING] Human-readable short labels for zone stripe headers
const ZONE_LABEL = {
  InvoiceLineDate:      "Date",
  InvoiceLineInvoiceID: "Invoice #",
  InvoiceLinePoNo:      "PO #",
  InvoiceLineAmount:    "Amount",
};

// [ZONE MATCHING] Tokens whose text matches these patterns define the bottom of the invoice table.
// First matching token below the header = footer boundary; body tokens at/below that y are excluded.
// IMPORTANT: 'balance' is intentionally NOT matched as a standalone word — "Balance Forward" /
// "Balance Brought Forward" / "Balance B/F" are body lines (transactions carried over from a
// prior statement), not footers. Only true footer phrases like "Balance Due" / "Balance Owing" /
// "Balance Outstanding" / "Balance Payable" should match.
const FOOTER_PATTERNS = /\b(?:total|current|aging|remit|please\s+pay|amount\s+due|owing|past\s+due|\d+\s*days?|net\s+\d+|balance\s+(?:due|owing|outstanding|payable))\b/i;

// [ZONE MATCHING] Renders colored column zone stripes and draggable separator handles
// on the PDF overlay. Stripes span the detected table body area (stripeTopNorm → stripeBottomNorm).
// Outer div has no zIndex so it doesn't create a stacking context — zone stripes (no zIndex)
// sit at layer 6 (behind bounding boxes at zIndex 5), separator handles at zIndex 8 sit above
// bounding boxes and the draw preview (zIndex 7).
// activeField + onZoneClick: clicking a stripe label sets that column as active (others dim),
// matching the existing offcanvas label-click → highlight behaviour.
function ColumnZoneOverlay({
  columnZones, separatorPositions,
  containerWidth, containerHeight,
  stripeTopNorm, stripeBottomNorm,
  onSeparatorMouseDown,
  activeField,
  onZoneClick,
}) {
  if (!columnZones?.length || !containerWidth || !containerHeight) return null;

  const topPx    = stripeTopNorm    * containerHeight;
  const heightPx = Math.max(2, (stripeBottomNorm - stripeTopNorm) * containerHeight);

  // [ZONE MATCHING] If the active field belongs to one of our zone columns, dim the others
  const activeZoneId = columnZones.some((z) => z.id === activeField) ? activeField : null;

  return (
    <div className="position-absolute top-0 start-0 w-100 h-100"
         style={{ pointerEvents: "none" }}>

      {columnZones.map((col) => {
        const color    = ZONE_COLORS[col.labelType] || ZONE_COLORS.default;
        const leftPx   = col.zoneLeft  * containerWidth;
        const wPx      = Math.max(1, (col.zoneRight - col.zoneLeft) * containerWidth);
        const label    = ZONE_LABEL[col.labelType] ?? col.labelType;
        // [ZONE MATCHING] Dim non-active columns when one is focused
        const isActive = !activeZoneId || col.id === activeZoneId;
        return (
          <div key={`zone-${col.id}`} style={{
            position:    "absolute",
            top:         `${topPx}px`,
            left:        `${leftPx}px`,
            width:       `${wPx}px`,
            height:      `${heightPx}px`,
            background:  isActive ? `${color}18` : `${color}08`,
            borderLeft:  `1px solid ${isActive ? color + "66" : color + "28"}`,
            borderRight: `1px solid ${isActive ? color + "66" : color + "28"}`,
            boxSizing:   "border-box",
            overflow:    "hidden",
            opacity:     isActive ? 1 : 0.35,
            transition:  "opacity 0.15s, background 0.15s",
          }}>
            {/* [ZONE MATCHING] Clickable label — sets this column as the active field,
                dimming other stripes and highlighting the heading box (same as offcanvas click) */}
            <span
              title={`${label} — click to focus`}
              style={{
                display:       "block",
                fontSize:      "9px",
                fontWeight:    600,
                color,
                padding:       "2px 4px",
                whiteSpace:    "nowrap",
                textAlign:     "center",
                opacity:       0.9,
                cursor:        "pointer",
                pointerEvents: "auto",
                userSelect:    "none",
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onZoneClick(col.id);
              }}
            >{label}</span>
          </div>
        );
      })}

      {separatorPositions.map((sep) => (
        <div
          key={sep.key}
          title={sep.hint}
          aria-label={`${sep.hint} — drag to resize`}
          style={{
            position:       "absolute",
            top:            `${topPx}px`,
            left:           `${sep.x * containerWidth}px`,
            width:          "12px",
            height:         `${heightPx}px`,
            transform:      "translateX(-6px)",
            cursor:         "col-resize",
            pointerEvents:  "auto",
            display:        "flex",
            justifyContent: "center",
            zIndex:         8,
          }}
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onSeparatorMouseDown(e, sep.key, sep.x, sep.pairedKey, sep.minX, sep.maxX);
          }}
        >
          <div style={{ width: "2px", height: "100%", background: "#334155", opacity: 0.65 }} />
        </div>
      ))}
    </div>
  );
}

function PassiveOverlayBoxes({ boxes, containerWidth, containerHeight, currentPage, onBoxClick }) {
  if (!boxes?.length || !containerWidth || !containerHeight) return null;

  return (
    <div className="position-absolute top-0 start-0 w-100 h-100" style={{ pointerEvents: "auto", zIndex: 5 }} aria-hidden="true">
      {boxes
        .filter((box) => !box?.page || Number(box.page) === Number(currentPage))
        .map((box, index) => {
          const x = Number(box?.bbox?.x ?? 0);
          const y = Number(box?.bbox?.y ?? 0);
          const x2 = Number(box?.bbox?.x2 ?? box?.bbox?.x ?? 0);
          const y2 = Number(box?.bbox?.y2 ?? box?.bbox?.y ?? 0);

          const leftPx = x * containerWidth;
          const topPx = y * containerHeight;
          const widthPx = Math.max((x2 - x) * containerWidth, 2);
          const heightPx = Math.max((y2 - y) * containerHeight, 2);

          return (
            <div
              key={`${box.type}-${box.fieldId || box.text_id}-${index}`}
              onClick={(e) => {
                e.stopPropagation();
                if (onBoxClick && box?.fieldId) onBoxClick(box.fieldId);
              }}
              style={{
                position: "absolute",
                left: `${leftPx}px`,
                top: `${topPx}px`,
                width: `${widthPx}px`,
                height: `${heightPx}px`,
                border: `2px solid ${box.color}`,
                background: `${box.color}33`,
                pointerEvents: "auto",
                cursor: onBoxClick && box?.fieldId ? "pointer" : "default",
                boxSizing: "border-box",
              }}
            />
          );
        })}
    </div>
  );
}

export default function InvoiceStatementConfigureModal({ show, onHide, fileName, statementImageData, docObj, dpaId, invStatId, supplierResolutionDraft }) {
  const { triggerToast } = useToast();
  const { fetchData } = useFetchData();
  const fetchDataRef = useRef(fetchData);
  const triggerToastRef = useRef(triggerToast);
  const [isLoadingOnboard, setIsLoadingOnboard] = useState(false);
  const [onboardData, setOnboardData] = useState(null);
  
  // PDF state - from PdfModal
  const [scale, setScale] = useState(1.0);
  const [pageNo, setPageNo] = useState(1);
  const [numPages, setNumPages] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageDimensions, setPageDimensions] = useState({ width: 0, height: 0 });
  const canvasRef = useRef(null);
  const scrollRef = useRef(0);
  const [isDragging, setIsDragging] = useState(false);
  const dragRef = useRef(null);

  useEffect(() => {
    fetchDataRef.current = fetchData;
  }, [fetchData]);

  useEffect(() => {
    triggerToastRef.current = triggerToast;
  }, [triggerToast]);
  
  // Right panel width state
  const [rightPanelWidth, setRightPanelWidth] = useState(360);
  const [activeField, setActiveField] = useState("");
  const [hoveredField, setHoveredField] = useState("");
  const [validatedFields, setValidatedFields] = useState({});
  const [deletedFields, setDeletedFields] = useState({});
  const [fieldOverrides, setFieldOverrides] = useState({});
  const [drawFieldId, setDrawFieldId] = useState("");
  const [drawStart, setDrawStart] = useState(null);
  const [drawPreview, setDrawPreview] = useState(null);
  // [ZONE MATCHING] In-memory separator overrides for the current session (cleared on close).
  // Ground truth is zone_x_left/zone_x_right in the saved column payload — see columnZones memo.
  const [separatorOverrides, setSeparatorOverrides] = useState({});
  // [ZONE MATCHING] Active drag state for separator handle dragging
  const [draggingSep, setDraggingSep] = useState(null);
  const [activeStep, setActiveStep] = useState("labels");
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [saveFeedback, setSaveFeedback] = useState(null);
  const [isPublishing, setIsPublishing] = useState(false);
  const [publishResult, setPublishResult] = useState(null);
  const [canPublish, setCanPublish] = useState(false);
  const [publishMissing, setPublishMissing] = useState([]);
  const latestDraftPayloadRef = useRef(null);
  const hasHydratedOnboardRef = useRef(false);
  const [showSupplierAddOverlay, setShowSupplierAddOverlay] = useState(false);
  const [supplierAddKind, setSupplierAddKind] = useState("parent");
  const [supplierAdditions, setSupplierAdditions] = useState({ parent: null, trading: null });
  const [supplierSelections, setSupplierSelections] = useState({ parent: "", trading: "" });
  const [supplierSelectedRecords, setSupplierSelectedRecords] = useState({ parent: null, trading: null });
  const [payeeGate, setPayeeGate] = useState({ status: "idle", hit: null, scanned: "", slot: null });
  const [showPayeePicker, setShowPayeePicker] = useState(false);
  // Write-only for now: retained so the last picker result set survives for future merge UX.
  const [, setPayeePickerResults] = useState([]);
  const [mergeCandidates, setMergeCandidates] = useState([]);   // [{id, name}]
  const [mergeRequests, setMergeRequests] = useState([]);        // [master_id]
  const [pendingRelink, setPendingRelink] = useState(null);      // picker record for alias-hit Change
  const [supplierAddDraft, setSupplierAddDraft] = useState({
    name: "",
    street: "",
    city: "",
    province: "",
    postal_code: "",
    country: "",
  });
  const isRowPayloadItem = (item) => {
    return Boolean(
      item
      && typeof item === "object"
      && item.type
      && Object.prototype.hasOwnProperty.call(item, "data")
    );
  };

  const rootPayload = useMemo(() => {
    if (!onboardData) return {};

    if (Array.isArray(onboardData)) {
      const first = onboardData[0];
      if (first && typeof first === "object" && !isRowPayloadItem(first) && !Array.isArray(first)) {
        return first;
      }
      return {};
    }

    return typeof onboardData === "object" ? onboardData : {};
  }, [onboardData]);

  const rowPayloadItems = useMemo(() => {
    if (Array.isArray(onboardData)) {
      const directRows = onboardData.filter((item) => isRowPayloadItem(item));
      if (directRows.length) return directRows;

      const nestedRows = Array.isArray(rootPayload?.layout)
        ? rootPayload.layout.filter((item) => isRowPayloadItem(item))
        : [];
      return nestedRows;
    }

    return Array.isArray(rootPayload?.layout)
      ? rootPayload.layout.filter((item) => isRowPayloadItem(item))
      : [];
  }, [onboardData, rootPayload]);

  const hasRowPayload = rowPayloadItems.length > 0;

  const normalizedSavedOnboarding = useMemo(() => {
    const toObject = (value) => (value && typeof value === "object" && !Array.isArray(value) ? value : {});

    const confirmedFromLayout = hasRowPayload
      ? toObject(rowPayloadItems.find((item) => item?.type === "confirmed_labels")?.data)
      : {};
    const draftFromLayout = hasRowPayload
      ? toObject(rowPayloadItems.find((item) => item?.type === "confirmation_draft")?.data)
      : {};

    const legacyConfirmed = toObject(rootPayload?.confirmed_labels);
    const legacyDraft = toObject(rootPayload?.confirmation_draft || legacyConfirmed?.confirmation_draft);

    // Authoritative source for saved onboarding is layout.confirmed_labels + layout.confirmation_draft when available.
    const confirmed_labels = Object.keys(confirmedFromLayout).length > 0 ? confirmedFromLayout : legacyConfirmed;
    const confirmation_draft = Object.keys(draftFromLayout).length > 0 ? draftFromLayout : legacyDraft;
    const supplier_selection = toObject(confirmation_draft?.supplier_selection);

    return {
      confirmed_labels,
      confirmation_draft,
      supplier_selection,
    };
  }, [hasRowPayload, rowPayloadItems, rootPayload]);

  const savedConfirmedLabels = normalizedSavedOnboarding.confirmed_labels;
  const savedConfirmationDraft = normalizedSavedOnboarding.confirmation_draft;
  const savedSupplierSelection = normalizedSavedOnboarding.supplier_selection;

  const hasSavedConfirmedData = useMemo(() => {
    const confirmedLabels = Array.isArray(savedConfirmedLabels?.labels) ? savedConfirmedLabels.labels : [];
    const confirmedHeadings = Array.isArray(savedConfirmedLabels?.tableHeadings) ? savedConfirmedLabels.tableHeadings : [];
    return confirmedLabels.length > 0 || confirmedHeadings.length > 0;
  }, [savedConfirmedLabels]);

  const profile = useMemo(() => {
    if (hasRowPayload) {
      const profileRow = rowPayloadItems.find((item) => item?.type === "profile");
      return profileRow?.data || {};
    }
    return rootPayload?.profile || rootPayload || {};
  }, [hasRowPayload, rowPayloadItems, rootPayload]);


  const INVOICE_FIELD_ORDER = {
    InvoiceLineDate: 0,
    InvoiceLineInvoiceID: 1,
    InvoiceLinePoNo: 2,
    InvoiceLineAmount: 3,
  };

  const buildInvoiceMappingsFromClaude = (layoutItems = [], tokensById = {}) => {
    const fields = layoutItems
      .filter((item) => item?.__type === "onboarding_field")
      .filter((field) => field?.label && Array.isArray(field.text_ids) && field.text_ids.length > 0)
      .filter((field) => Object.prototype.hasOwnProperty.call(INVOICE_FIELD_ORDER, field.label));

    return fields
      .sort((a, b) => INVOICE_FIELD_ORDER[a.label] - INVOICE_FIELD_ORDER[b.label])
      .map((field) => {
        const fieldIndex = INVOICE_FIELD_ORDER[field.label];
        const fieldId = `invoice_mapping_${fieldIndex}`;
        const tokens = field.text_ids.map((id) => tokensById[id]).filter(Boolean);

        const xs = tokens.flatMap((t) => [t?.bbox?.x, t?.bbox?.x2]).filter(Number.isFinite);
        const ys = tokens.flatMap((t) => [t?.bbox?.y, t?.bbox?.y2]).filter(Number.isFinite);

        const bbox =
          xs.length && ys.length
            ? {
                x: Math.min(...xs),
                y: Math.min(...ys),
                x2: Math.max(...xs),
                y2: Math.max(...ys),
                page: field.page ?? tokens[0]?.page ?? 1,
              }
            : null;

        return {
          field_id: fieldId,
          labelType: field.label,
          text: field.text,
          matchText: [field.text],
          text_ids: field.text_ids,
          page: field.page ?? tokens[0]?.page ?? 1,
          bbox,
          x_min: bbox?.x ?? null,
          x_mid: bbox ? (bbox.x + bbox.x2) / 2 : null,
          x_max: bbox?.x2 ?? null,
          column_index: fieldIndex,
          confidence: 0.9,
          source: "claude",
          confirmed: false,
          edited: false,
          original_detected: {
            text: field.text,
            text_ids: field.text_ids,
            bbox,
          },
        };
      });
  };


  const layoutItems = useMemo(() => {
    const items = hasRowPayload
      ? rowPayloadItems.filter((item) => item?.type !== "confirmed_labels" && item?.type !== "confirmation_draft")
      : (Array.isArray(rootPayload?.layout) ? rootPayload.layout : []);

    return items
      .filter((item) => item && item.type && item.data)
      .map((item) => ({
        __type: item.type,
        ...item.data,
      }));
  }, [hasRowPayload, rowPayloadItems, rootPayload]);

  const layoutTokens = useMemo(() => layoutItems.filter((item) => item.__type === "tokens"), [layoutItems]);
  const layoutLabels = useMemo(() => layoutItems.filter((item) => item.__type === "labels"), [layoutItems]);
  const layoutLines = useMemo(() => layoutItems.filter((item) => item.__type === "lines"), [layoutItems]);
  const layoutTables = useMemo(() => layoutItems.filter((item) => item.__type === "table"), [layoutItems]);
  const layoutTableHeadings = useMemo(() => {
    const all = layoutItems.filter((item) => item.__type === "table_headings");
    if (!all.length) return null;
    const current = all.find((item) => Number(item.page ?? 1) === Number(currentPage));
    return current ?? all[0];
  }, [layoutItems, currentPage]);
  // [ZONE MATCHING] True when DocAI detected a table on the currently displayed page.
  // Used to gate ColumnZoneOverlay so stripes/separator handles never render on pages
  // without a real table (cover/summary pages, trailing blanks). Prevents the user
  // from dragging zones against an empty layout, which previously produced wide bands.
  const currentPageHasTable = useMemo(() => {
    const layoutMatch = layoutItems.find(
      (item) => item.__type === "table_headings" &&
                Number(item.page ?? 1) === Number(currentPage)
    );
    return Array.isArray(layoutMatch?.columns) && layoutMatch.columns.length > 0;
  }, [layoutItems, currentPage]);
  const layoutTableColumns = useMemo(
    () => layoutItems.filter((item) => item.__type === "table_columns"),
    [layoutItems]
  );
  // Handles profile.tableHeadings as Array OR plain Object.
  // Choose the richest heading set (saved/profile/layout) so partial saved drafts do not hide Date/Amount columns.
  const resolvedTableHeadings = useMemo(() => {
    const toHeading = (value) => (Array.isArray(value) ? (value[0] || null) : value || null);

    const liveLayoutHeading = toHeading(layoutTableHeadings);
    if (liveLayoutHeading && Array.isArray(liveLayoutHeading?.columns) && liveLayoutHeading.columns.length) {
      return liveLayoutHeading;
    }

    const savedHeading = hasSavedConfirmedData
      ? toHeading(Array.isArray(savedConfirmedLabels?.tableHeadings) ? savedConfirmedLabels.tableHeadings : null)
      : null;
    const profileHeading = toHeading(profile?.tableHeadings);
    const layoutHeading = toHeading(layoutTableHeadings);

    const headingScore = (heading, sourceRank) => {
      if (!heading || !Array.isArray(heading?.columns) || !heading.columns.length) return -1;
      const columns = heading.columns;
      const canonicalTypes = new Set([
        "InvoiceLineDate",
        "InvoiceLineInvoiceID",
        "InvoiceLinePoNo",
        "InvoiceLineAmount",
      ]);
      const typeCoverage = new Set(columns.map((column) => column?.labelType).filter((value) => canonicalTypes.has(value))).size;

      const geometricColumns = columns.filter(
        (column) => Number.isFinite(Number(column?.x_min)) && Number.isFinite(Number(column?.x_max))
      ).length;

      let score = 0;
      score += Math.min(columns.length, 8) * 10;
      score += typeCoverage * 25;
      score += Math.min(geometricColumns, 8) * 5;
      score += sourceRank; // tie-breaker only
      return score;
    };

    const candidates = [
      { heading: savedHeading, rank: 1 },
      { heading: profileHeading, rank: 2 },
      { heading: layoutHeading, rank: 3 },
    ];

    const best = candidates
      .map((item) => ({ ...item, score: headingScore(item.heading, item.rank) }))
      .sort((a, b) => b.score - a.score)[0];

    return best?.score >= 0 ? best.heading : null;
  }, [hasSavedConfirmedData, savedConfirmedLabels, profile?.tableHeadings, layoutTableHeadings]);

  const sourceTokens = useMemo(() => (layoutTokens.length ? layoutTokens : profile?.tokens || []), [layoutTokens, profile?.tokens]);

  const tableSource = useMemo(() => {
    return rootPayload?.table || profile?.table || layoutTables?.[0] || {};
  }, [rootPayload?.table, profile?.table, layoutTables]);

  const resolvedMappedColumns = useMemo(() => {
    const mapped = tableSource?.mapped_columns || {};

    const fromMappedColumns = {
      InvoiceLineDate: mapped.invoice_date || null,
      InvoiceLineInvoiceID: mapped.invoice_id || null,
      InvoiceLinePoNo: mapped.po_reference || null,
      InvoiceLineAmount: mapped.invoice_amount || null,
    };

    const fromLayoutColumns = {};
    layoutTableColumns.forEach((column) => {
      if (!column?.labelType) return;
      if (![
        "InvoiceLineDate",
        "InvoiceLineInvoiceID",
        "InvoiceLinePoNo",
        "InvoiceLineAmount",
      ].includes(column.labelType)) return;

      fromLayoutColumns[column.labelType] = {
        ...column,
        selection_type: "table_heading",
        selectionType: "column",
      };
    });

    return {
      InvoiceLineDate: fromMappedColumns.InvoiceLineDate || fromLayoutColumns.InvoiceLineDate || null,
      InvoiceLineInvoiceID: fromMappedColumns.InvoiceLineInvoiceID || fromLayoutColumns.InvoiceLineInvoiceID || null,
      InvoiceLinePoNo: fromMappedColumns.InvoiceLinePoNo || fromLayoutColumns.InvoiceLinePoNo || null,
      InvoiceLineAmount: fromMappedColumns.InvoiceLineAmount || fromLayoutColumns.InvoiceLineAmount || null,
    };
  }, [tableSource, layoutTableColumns]);

  const tokenMap = useMemo(() => {
    const map = {};
    sourceTokens.forEach((token) => {
      if (token?.key) map[token.key] = token;
    });
    return map;
  }, [sourceTokens]);

  const claudeInvoiceMappings = useMemo(() => {
    return buildInvoiceMappingsFromClaude(layoutItems, tokenMap);
  }, [layoutItems, tokenMap]);

  const tokenText = (token) => token?.text || token?.value || token?.word || "";

  const sortTokensInReadingOrder = (tokens = []) => {
    return [...tokens].sort((a, b) => {
      const aPage = Number(a?.page || a?.bbox?.page || 1);
      const bPage = Number(b?.page || b?.bbox?.page || 1);
      if (aPage !== bPage) return aPage - bPage;

      const aY = Number(a?.bbox?.y);
      const bY = Number(b?.bbox?.y);
      const yDelta = Math.abs(aY - bY);
      if (Number.isFinite(aY) && Number.isFinite(bY) && yDelta > 0.0035) {
        return aY - bY;
      }

      const aX = Number(a?.bbox?.x);
      const bX = Number(b?.bbox?.x);
      if (Number.isFinite(aX) && Number.isFinite(bX) && aX !== bX) {
        return aX - bX;
      }

      if (Number.isFinite(aY) && Number.isFinite(bY) && aY !== bY) {
        return aY - bY;
      }

      return 0;
    });
  };

  const normalizeReconstructedText = (value) => {
    return String(value || "")
      .replace(/\s+/g, " ")
      .replace(/\b([A-Za-z])\s*\/\s*([A-Za-z])\b/g, "$1/$2")
      .trim();
  };

  const getTokensFromIds = (ids = []) => {
    const uniqueIds = [...new Set(Array.isArray(ids) ? ids.filter(Boolean) : [])];
    return sortTokensInReadingOrder(
      uniqueIds
        .map((id) => tokenMap[id])
        .filter(Boolean)
    );
  };

  const getTextFromIds = (ids = []) => {
    return normalizeReconstructedText(
      getTokensFromIds(ids)
        .map((token) => tokenText(token))
        .filter(Boolean)
        .join(" ")
    );
  };

  const getLabelDisplayText = (label) => {
    if (label?.text) return label.text;
    const ids = Array.isArray(label?.text_ids) ? label.text_ids : label?.text_id ? [label.text_id] : [];
    const text = ids
      .map((id) => tokenMap[id])
      .filter(Boolean)
      .map((token) => tokenText(token))
      .filter(Boolean)
      .join(" ")
      .trim();
    return text || "—";
  };

  const getBoxesFromTokenIds = (ids = []) => {
    if (!Array.isArray(ids) || !ids.length) return [];

    const mergedBoxes = ids
      .map((id) => tokenMap[id])
      .filter(Boolean)
      .map((token) => {
        const box = {
          x: token?.bbox?.x,
          y: token?.bbox?.y,
          x2: token?.bbox?.x2,
          y2: token?.bbox?.y2,
          page: token?.page,
        };
        return [box.x, box.y, box.x2, box.y2].every(Number.isFinite) ? box : null;
      })
      .filter(Boolean);

    const merged = mergeBoxes(mergedBoxes);
    return merged ? [merged] : [];
  };

  const normalizeBbox = (bbox) => {
    if (!bbox) return undefined;

    const x = Number(bbox?.x);
    const y = Number(bbox?.y);
    const x2 = Number(bbox?.x2);
    const y2 = Number(bbox?.y2);
    const page = Number(bbox?.page);

    if (![x, y, x2, y2].every(Number.isFinite)) return undefined;

    return {
      x,
      y,
      x2,
      y2,
      ...(Number.isFinite(page) ? { page } : {}),
    };
  };

  const normalizeSupplierResolutionForSave = (resolution) => {
    if (!resolution || typeof resolution !== "object") return null;

    // Backend placeholders ("Not detected") must never persist as observed names —
    // publish upserts observed_names as aliases, and a "not detected" alias would
    // falsely match every future statement with an undetected name.
    const observed_names = Array.isArray(resolution?.observed_names)
      ? resolution.observed_names.filter((row) => {
          const v = String(row?.value || "").trim();
          return v && v !== "Not detected";
        })
      : resolution?.observed_names;

    const address = resolution?.address || {};
    const raw = String(address?.raw || "").trim();
    const street = String(address?.street || "").trim();
    const city = String(address?.city || "").trim();
    const province = String(address?.province || "").trim().toUpperCase();
    const postal_code = String(address?.postal_code || "").trim().toUpperCase();
    const parsed = (!city && (raw || street)) ? parseCanadianAddress(raw || street) || {} : {};

    return {
      ...resolution,
      observed_names,
      address: {
        ...address,
        street: parsed?.street || street,
        city: parsed?.city || city,
        province: parsed?.province || province,
        postal_code: parsed?.postal_code || postal_code,
        country: normalizeCountry(parsed?.country || address?.country || ""),
        raw,
      },
    };
  };

  const savedStatementDraftRows = useMemo(() => {
    const confirmed = Array.isArray(savedConfirmedLabels?.labels) ? savedConfirmedLabels.labels : [];
    const ignored = Array.isArray(savedConfirmedLabels?.ignored_labels) ? savedConfirmedLabels.ignored_labels : [];
    return [...confirmed, ...ignored];
  }, [savedConfirmedLabels]);

  const savedInvoiceDraftRows = useMemo(() => {
    const headings = Array.isArray(savedConfirmedLabels?.tableHeadings) ? savedConfirmedLabels.tableHeadings : [];
    const confirmed = headings.flatMap((heading) => Array.isArray(heading?.columns) ? heading.columns : []);
    const ignored = Array.isArray(savedConfirmedLabels?.ignored_columns) ? savedConfirmedLabels.ignored_columns : [];
    return [...confirmed, ...ignored];
  }, [savedConfirmedLabels]);

  const getSavedStatementDraft = (fieldId, labelName) => {
     return savedStatementDraftRows.find((row) => row?.field_id === fieldId) || null;
  };

  const buildStableStatementFieldId = (labelName, fallbackIndex) => {
    const semantic = String(labelName || "").trim().toLowerCase();
    if (!semantic) return `statement_label_${fallbackIndex}`;
    const safe = semantic.replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
    return `statement_label_${safe || fallbackIndex}`;
  };

  const getSavedInvoiceDraft = (fieldId, labelType) => {
    return savedInvoiceDraftRows.find((row) => row?.field_id === fieldId)
      || savedInvoiceDraftRows.find((row) => row?.labelType === labelType)
      || null;
  };

  const statementCards = useMemo(() => {
    const priority = ["SupplierParentName", "SupplierTradingName", "StatementDate", "StatementTerms"];
    const confirmedLabelRows = Array.isArray(savedConfirmedLabels?.labels) ? savedConfirmedLabels.labels : [];
    const sourceLabels = hasSavedConfirmedData
      ? confirmedLabelRows
      : (layoutLabels.length ? layoutLabels : profile?.labels || []);

    const seenLabels = new Set();
    const uniqueLabels = sourceLabels.filter((label, index) => {
      const labelName = String(label?.label || "").trim();
      if (!labelName) return true;

      const page = Number(label?.page || label?.bbox?.page || 1);
      const bbox = label?.bbox || {};
      const key = [
        labelName,
        page,
        Number(bbox?.x || 0).toFixed(5),
        Number(bbox?.y || 0).toFixed(5),
        Number(bbox?.x2 || 0).toFixed(5),
        Number(bbox?.y2 || 0).toFixed(5),
        Array.isArray(label?.text_ids) ? label.text_ids.join("|") : index,
      ].join("::");

      if (seenLabels.has(key)) return false;
      seenLabels.add(key);
      return true;
    });

    const isHeaderLabel = (label) => {
      const ids = Array.isArray(label?.text_ids) ? label.text_ids : label?.text_id ? [label.text_id] : [];
      if (!ids.length) return false;
      return ids.some((id) => String(tokenMap[id]?.type || "").toLowerCase() === "header");
    };

    const labels = uniqueLabels.map((label, index) => {
      const semanticLabel = String(label?.label || "").trim();
      const page = Number(label?.page || label?.bbox?.page || 1);
      const generatedId = label?.field_id || `${buildStableStatementFieldId(semanticLabel, index)}_p${page}_${index}`;

      const savedDraft = getSavedStatementDraft(generatedId, semanticLabel);
      const resolvedId = String(savedDraft?.field_id || generatedId);
      const overrideIds = fieldOverrides?.[resolvedId]?.text_ids;
      const savedTextIds = Array.isArray(savedDraft?.text_ids) ? savedDraft.text_ids : [];
      const originalSavedTextIds = Array.isArray(savedDraft?.original_detected?.text_ids) ? savedDraft.original_detected.text_ids : [];
      const currentTextIds = Array.isArray(overrideIds) ? overrideIds : (savedTextIds.length ? savedTextIds : label?.text_ids);
      const overrideText = Array.isArray(overrideIds)
        ? overrideIds
            .map((tokenId) => tokenMap[tokenId])
            .filter(Boolean)
            .map((token) => tokenText(token))
            .filter(Boolean)
            .join(" ")
            .trim()
        : "";

      return {
        ...label,
        id: resolvedId,
        field_id: resolvedId,
        text_ids: currentTextIds,
        original_text_ids: originalSavedTextIds.length
          ? originalSavedTextIds
          : Array.isArray(label?.text_ids)
            ? label.text_ids
            : label?.text_id
              ? [label.text_id]
              : [],
        displayName: `${label?.label || `Label ${index + 1}`} (Page ${page})`,
        displayValue: overrideText || savedDraft?.text || getLabelDisplayText(label),
        original_displayValue: savedDraft?.original_detected?.text || getLabelDisplayText(label),
        bbox: savedDraft?.bbox || label?.bbox,
        page: savedDraft?.page || label?.page,
        isHeaderLabel: isHeaderLabel(label),
      };
    });

    const extraSavedLabels = savedStatementDraftRows
      .filter((row) => row?.field_id && !labels.some((item) => item?.field_id === row.field_id))
      .map((row, index) => ({
        ...row,
        id: row?.field_id || `saved_statement_label_${index}`,
        field_id: row?.field_id || `saved_statement_label_${index}`,
        label: row?.label,
        text_ids: Array.isArray(row?.text_ids) ? row.text_ids : [],
        original_text_ids: Array.isArray(row?.original_detected?.text_ids) ? row.original_detected.text_ids : [],
        displayName: row?.label || `Saved Label ${index + 1}`,
        displayValue: row?.text || "—",
        original_displayValue: row?.original_detected?.text || row?.text || "—",
        bbox: row?.bbox,
        page: row?.page,
        isHeaderLabel: false,
      }));

    return [...labels, ...extraSavedLabels].sort((a, b) => {
      const aPriority = priority.indexOf(a.label);
      const bPriority = priority.indexOf(b.label);
      if (aPriority === -1 && bPriority === -1) return 0;
      if (aPriority === -1) return 1;
      if (bPriority === -1) return -1;
      return aPriority - bPriority;
    });
  }, [hasSavedConfirmedData, savedConfirmedLabels, layoutLabels, profile?.labels, tokenMap, fieldOverrides, savedStatementDraftRows]);

  const normalize = (value) => String(value || "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();

  // Only use profile.tableHeadings[0].columns for invoice onboarding
  const getColumnsFromTableSource = () => {
    const columns = Array.isArray(resolvedTableHeadings?.columns) ? resolvedTableHeadings.columns : [];
    const canonicalTypes = [
      "InvoiceLineDate",
      "InvoiceLineInvoiceID",
      "InvoiceLinePoNo",
      "InvoiceLineAmount"
    ];

    const byCanonical = {};
    canonicalTypes.forEach((type) => {
      const claudeMapped = claudeInvoiceMappings.find((item) => item?.labelType === type);
      const mapped = claudeMapped || resolvedMappedColumns?.[type];
      if (!mapped) return;

      byCanonical[type] = {
        ...mapped,
        labelType: type,
        matchText: Array.isArray(mapped?.matchText)
          ? mapped.matchText
          : [mapped?.text].filter(Boolean),
        selectionType: "column",
      };
    });

    columns.forEach((column) => {
      const canonical = canonicalTypes.includes(column?.labelType) ? column.labelType : null;
      if (!canonical || byCanonical[canonical]) return;
      byCanonical[canonical] = {
        ...column,
        labelType: canonical,
      };
    });

    const usable = columns
      .filter((column) => Number.isFinite(Number(column?.x_min)) || Number.isFinite(Number(column?.x_mid)) || Number.isFinite(Number(column?.x_max)))
      .map((column) => {
        const xMin = Number(column?.x_min);
        const xMid = Number(column?.x_mid);
        const xMax = Number(column?.x_max);
        const sortX = Number.isFinite(xMid)
          ? xMid
          : Number.isFinite(xMin) && Number.isFinite(xMax)
            ? (xMin + xMax) / 2
            : Number.isFinite(xMin)
              ? xMin
              : xMax;
        return { ...column, __sortX: sortX };
      })
      .filter((column) => Number.isFinite(column.__sortX))
      .sort((a, b) => a.__sortX - b.__sortX);

    // const usedColumns = new Set(Object.values(byCanonical).map((column) => column));
    // const nextUnusedFromLeft = () => usable.find((column) => !usedColumns.has(column));
    // const nextUnusedFromRight = () => [...usable].reverse().find((column) => !usedColumns.has(column));
    const colKey = (col) => `${col?.page ?? 1}:${Number.isFinite(Number(col?.column_index)) ? col.column_index : (col?.x_mid ?? col?.x_min)}`;
    const usedColumnKeys = new Set(Object.values(byCanonical).map(colKey));
    const nextUnusedFromLeft = () => usable.find((col) => !usedColumnKeys.has(colKey(col)));
    const nextUnusedFromRight = () => [...usable].reverse().find((col) => !usedColumnKeys.has(colKey(col)));

    const nthFromLeft = (n) => usable[Math.max(0, Math.min(usable.length - 1, n))] || null;

    if (!byCanonical.InvoiceLineDate) {
      const col = nextUnusedFromLeft();
      if (col) {
        byCanonical.InvoiceLineDate = { ...col, labelType: "InvoiceLineDate" };
        usedColumnKeys.add(colKey(col));
      }
    }

    if (!byCanonical.InvoiceLineAmount) {
      const col = nextUnusedFromRight();
      if (col) {
        byCanonical.InvoiceLineAmount = { ...col, labelType: "InvoiceLineAmount" };
        usedColumnKeys.add(colKey(col));
      }
    }

    if (!byCanonical.InvoiceLineInvoiceID) {
      const col = nextUnusedFromLeft();
      if (col) {
        byCanonical.InvoiceLineInvoiceID = { ...col, labelType: "InvoiceLineInvoiceID" };
        usedColumnKeys.add(colKey(col));
      }
    }

    // InvoiceLinePoNo is optional — no fallback assignment.
    // If Claude did not map it (returned empty text/text_ids), leave it unmapped so the user
    // can draw a bounding box to assign it explicitly. Guessing the next available column
    // (e.g. Due Date) is wrong and forces the user to fight the UI to correct it.
    // OLD (removed):
    // if (!byCanonical.InvoiceLinePoNo) {
    //   const col = nextUnusedFromLeft();
    //   if (col) {
    //     byCanonical.InvoiceLinePoNo = { ...col, labelType: "InvoiceLinePoNo" };
    //     usedColumnKeys.add(colKey(col));
    //   }
    // }

    // Hard fallback: always provide all canonical columns when any table geometry exists.
    if (usable.length) {
      if (!byCanonical.InvoiceLineDate) {
        const col = nthFromLeft(0);
        if (col) byCanonical.InvoiceLineDate = { ...col, labelType: "InvoiceLineDate" };
      }
      if (!byCanonical.InvoiceLineInvoiceID) {
        const col = nthFromLeft(1) || nthFromLeft(0);
        if (col) byCanonical.InvoiceLineInvoiceID = { ...col, labelType: "InvoiceLineInvoiceID" };
      }
      // InvoiceLinePoNo intentionally omitted — optional field, no hard fallback.
      if (!byCanonical.InvoiceLineAmount) {
        const col = nthFromLeft(usable.length - 1);
        if (col) byCanonical.InvoiceLineAmount = { ...col, labelType: "InvoiceLineAmount" };
      }
    }

    return canonicalTypes.map((type) => byCanonical[type]).filter(Boolean);
  };

  const invoiceColumnCards = useMemo(() => {
    const columns = getColumnsFromTableSource();
    return columns.map((column, index) => ({
      ...column,
      id: `invoice_column_${index}`,
      displayName: column?.labelType || column?.matchText?.join(" ") || `Column ${index + 1}`,
      displayValue: column?.matchText?.join(", ") || "—",
      confidence: Number(column?.confidence || column?.score || 0),
      selectionType: "column",
    }));
    }, [resolvedTableHeadings, resolvedMappedColumns, claudeInvoiceMappings]);

  const sharedInvoiceHeaderAnchor = useMemo(() => {
    const page = Number(resolvedTableHeadings?.page || 1);
    const columns = Array.isArray(resolvedTableHeadings?.columns) ? resolvedTableHeadings.columns : [];
    const lines = layoutLines.length ? layoutLines : profile?.lines || [];
    if (!columns.length || !lines.length) return { page, y: null, keys: [] };

    const headerTerms = columns
      .flatMap((column) => (Array.isArray(column?.matchText) ? column.matchText : [column?.matchText]))
      .map((value) => normalize(value))
      .filter(Boolean);

    const textMatchedLines = lines
      .filter((line) => Number(line?.page || 1) === page)
      .map((line) => {
        const y = Number(line?.y);
        const keys = Array.isArray(line?.keys) ? line.keys : [];
        const lineText = normalize(line?.text || "");
        if (!Number.isFinite(y) || !lineText || !keys.length) return null;
        const matchCount = headerTerms.filter((term) => lineText.includes(term)).length;
        return { y, keys, matchCount };
      })
      .filter(Boolean)
      .filter((line) => line.matchCount >= Math.min(3, headerTerms.length))
      .sort((a, b) => {
        if (b.matchCount !== a.matchCount) return b.matchCount - a.matchCount;
        return a.y - b.y;
      });

    if (textMatchedLines.length) {
      return {
        page,
        y: textMatchedLines[0].y,
        keys: textMatchedLines[0].keys,
      };
    }

    const usableColumns = columns.filter(
      (column) => Number.isFinite(Number(column?.x_min)) && Number.isFinite(Number(column?.x_max))
    );
    if (!usableColumns.length) return { page, y: null, keys: [] };

    const lineCandidates = lines
      .filter((line) => Number(line?.page || 1) === page)
      .map((line) => {
        const keys = Array.isArray(line?.keys) ? line.keys : [];
        const y = Number(line?.y);
        if (!Number.isFinite(y) || !keys.length) return null;

        const columnsHit = usableColumns.reduce((count, column) => {
          const xMin = Number(column.x_min) - 0.02;
          const xMax = Number(column.x_max) + 0.02;
          const hit = keys.some((key) => {
            const token = tokenMap[key];
            const x = Number(token?.bbox?.x);
            const x2 = Number(token?.bbox?.x2);
            if (!Number.isFinite(x) || !Number.isFinite(x2)) return false;
            const center = (x + x2) / 2;
            return center >= xMin && center <= xMax;
          });
          return count + (hit ? 1 : 0);
        }, 0);

        return { y, columnsHit, tokenCount: keys.length, keys };
      })
      .filter(Boolean)
      .filter((item) => item.columnsHit >= 3);

    if (lineCandidates.length) {
      const maxColumnsHit = Math.max(...lineCandidates.map((item) => item.columnsHit));
      const topMostBestLine = lineCandidates
        .filter((item) => item.columnsHit === maxColumnsHit)
        .sort((a, b) => a.y - b.y)[0];

      return {
        page,
        y: topMostBestLine?.y,
        keys: Array.isArray(topMostBestLine?.keys) ? topMostBestLine.keys : [],
      };
    }

    return { page, y: null, keys: [] };
  }, [resolvedTableHeadings, layoutLines, profile?.lines, tokenMap]);

  const tableHeaderYRange = useMemo(() => {
    const y = Number(sharedInvoiceHeaderAnchor?.y);
    if (Number.isFinite(y)) {
      return {
        min: Math.max(0, y - 0.012),
        max: Math.min(1, y + 0.014),
      };
    }
    return { min: 0.30, max: 0.34 };
  }, [sharedInvoiceHeaderAnchor]);

  const getColumnHeaderTokens = (field, tokens, page, tolerance = 0.03) => {
    if (!field || !Array.isArray(tokens) || !tokens.length) return [];
    if (!Number.isFinite(Number(field?.x_min)) || !Number.isFinite(Number(field?.x_max))) return [];

    const colXMin = Number(field.x_min);
    const colXMax = Number(field.x_max);
    const colPage = Number(page);

    const inColumn = tokens.filter((token) => {
      const tokenPage = Number(token?.page || 1);
      const tokenX = Number(token?.bbox?.x);
      const tokenX2 = Number(token?.bbox?.x2);
      const tokenY = Number(token?.bbox?.y);
      if (tokenPage !== colPage || !Number.isFinite(tokenX) || !Number.isFinite(tokenX2) || !Number.isFinite(tokenY)) return false;
      const tokenCentre = (tokenX + tokenX2) / 2;
      return tokenCentre >= colXMin - tolerance && tokenCentre <= colXMax + tolerance;
    });

    if (!inColumn.length) return [];

    const headerLineKeys =
      Number(sharedInvoiceHeaderAnchor?.page) === colPage && Array.isArray(sharedInvoiceHeaderAnchor?.keys)
        ? new Set(sharedInvoiceHeaderAnchor.keys)
        : null;

    if (headerLineKeys && headerLineKeys.size) {
      const byHeaderLineKeys = inColumn.filter((token) => headerLineKeys.has(token?.key));
      if (byHeaderLineKeys.length) return byHeaderLineKeys;
    }

    const sharedY = Number(sharedInvoiceHeaderAnchor?.page) === colPage
      ? Number(sharedInvoiceHeaderAnchor?.y)
      : NaN;
    const headerMid = Number.isFinite(sharedY)
      ? sharedY
      : (Number(tableHeaderYRange.min) + Number(tableHeaderYRange.max)) / 2;

    const strictBand = inColumn.filter((token) => {
      const y = Number(token?.bbox?.y);
      return Number.isFinite(y) && Math.abs(y - headerMid) <= 0.012;
    });

    if (strictBand.length) {
      return strictBand;
    }

    const relaxedBand = inColumn.filter((token) => {
      const y = Number(token?.bbox?.y);
      return Number.isFinite(y) && Math.abs(y - headerMid) <= 0.025;
    });

    if (relaxedBand.length) {
      return relaxedBand;
    }

    const anchor = [...inColumn]
      .filter((token) => Number.isFinite(Number(token?.bbox?.y)))
      .sort((a, b) => {
        const aY = Number(a?.bbox?.y);
        const bY = Number(b?.bbox?.y);
        const aDist = Math.abs(aY - headerMid);
        const bDist = Math.abs(bY - headerMid);
        if (aDist !== bDist) return aDist - bDist;
        return aY - bY;
      })[0];
    const anchorY = Number(anchor?.bbox?.y);
    if (!Number.isFinite(anchorY)) return [];

    const bandTokens = inColumn.filter((token) => {
      const tokenY = Number(token?.bbox?.y);
      return Number.isFinite(tokenY) && Math.abs(tokenY - anchorY) <= 0.012;
    });

    if (bandTokens.length) return bandTokens;

    return [anchor];
  };

  const getColumnTokenIds = (column, labelType, page) => {
    if (!column) {
       return [];
    }

    if (Array.isArray(column?.text_ids) && column.text_ids.length) {
       return column.text_ids
       .map((tokenId) => tokenMap[tokenId])
       .filter(Boolean)
       .filter((token) => Number(token?.page || 1) === Number(page || column?.page || 1))
       .map((token) => token?.key)
       .filter(Boolean);
    }

    if (!Number.isFinite(Number(column?.x_min)) || !Number.isFinite(Number(column?.x_max))) {
       return [];
    }

    const mappedField = {
      ...column,
      labelType,
      matchText: column?.matchText,
      x_min: Number(column?.x_min),
      x_max: Number(column?.x_max),
    };

    let resolvedIds = [];

    if (Array.isArray(column?.text_ids) && column.text_ids.length) {
      const providedTokens = column.text_ids
        .map((tokenId) => tokenMap[tokenId])
        .filter(Boolean);
      resolvedIds = getColumnHeaderTokens(mappedField, providedTokens, page)
        .map((token) => token?.key)
        .filter(Boolean);
    }

    if (!resolvedIds.length) {
      resolvedIds = getColumnHeaderTokens(mappedField, sourceTokens, page)
        .map((token) => token?.key)
        .filter(Boolean);
    }

    return resolvedIds;
  };

  // Only allow confirmation for the 4 canonical columns, mapped directly from invoiceColumnCards
  const invoiceMappings = useMemo(() => {
    const canonicalTypes = [
      { key: "InvoiceLineDate", label: "Invoice Date Column" },
      { key: "InvoiceLineInvoiceID", label: "Invoice ID Column" },
      { key: "InvoiceLinePoNo", label: "PO/Reference Column" },
      { key: "InvoiceLineAmount", label: "Invoice Amount Column" },
    ];
    const tableHeadingPage = resolvedTableHeadings?.page ?? 1;
    const orderedHeadingColumns = (Array.isArray(resolvedTableHeadings?.columns) ? resolvedTableHeadings.columns : [])
      .map((column) => {
        const xMin = Number(column?.x_min);
        const xMid = Number(column?.x_mid);
        const xMax = Number(column?.x_max);
        const sortX = Number.isFinite(xMid)
          ? xMid
          : Number.isFinite(xMin) && Number.isFinite(xMax)
            ? (xMin + xMax) / 2
            : Number.isFinite(xMin)
              ? xMin
              : xMax;
        return { ...column, __sortX: sortX };
      })
      .filter((column) => Number.isFinite(column?.__sortX))
      .sort((a, b) => a.__sortX - b.__sortX);
    //const allLines = layoutLines.length ? layoutLines : profile?.lines || [];

    return canonicalTypes.map((c, idx) => {
      const fieldId = `invoice_mapping_${idx}`;
      const mapped = invoiceColumnCards.find(col => col.labelType === c.key);
      const savedDraft = getSavedInvoiceDraft(fieldId, c.key);
      const savedGeometry = Number.isFinite(Number(savedDraft?.x_min)) && Number.isFinite(Number(savedDraft?.x_max))
        ? {
            x_min: Number(savedDraft.x_min),
            x_mid: Number(savedDraft?.x_mid),
            x_max: Number(savedDraft.x_max),
            page: Number(savedDraft?.page || tableHeadingPage),
            matchText: savedDraft?.matchText,
            text_ids: savedDraft?.text_ids,
            confidence: savedDraft?.detected_confidence,
            bbox: savedDraft?.bbox,
          }
        : null;

      // FIX: For optional columns (InvoiceLinePoNo), skip the positional index fallback when
      // there is no explicit Claude mapping and no previously saved draft geometry.
      // OLD: positionalGeometry was always used → idx=2 always resolved to Due Date for a
      //   7-column statement, permanently shadowing whatever the user drew.
      // OLD: const positionalGeometry = orderedHeadingColumns.length
      //        ? orderedHeadingColumns[Math.max(0, Math.min(orderedHeadingColumns.length - 1, idx))]
      //        : null;
      const OPTIONAL_COLUMN_TYPES = new Set(["InvoiceLinePoNo"]);
      const skipPositional = OPTIONAL_COLUMN_TYPES.has(c.key) && !mapped && !savedGeometry;
      const positionalGeometry = orderedHeadingColumns.length && !skipPositional
        ? orderedHeadingColumns[Math.max(0, Math.min(orderedHeadingColumns.length - 1, idx))]
        : null;
      const detectedColumn = mapped || positionalGeometry || null;
      const effectiveColumn = savedGeometry || detectedColumn;

      const effectivePage = Number(effectiveColumn?.page || tableHeadingPage);

      // FIX: React to fieldOverrides so the card and field data reflect user draws immediately.
      // OLD: invoiceMappings did not depend on fieldOverrides at all — after a draw,
      //   text_ids stayed as Due Date tokens, displayValue stayed "Due Date", and
      //   mapped stayed true with wrong geometry, causing the column text_ids block in
      //   getBoxesFromField to fire before the override check and return Due Date.
      // With this fix: text_ids = override tokens → card shows drawn text, getBoxesFromField
      //   uses the correct tokens even without the standalone override check, and
      //   serializeInvoiceColumn (which reads item.text_ids) saves the drawn geometry.
      const overrideTokenIds = fieldOverrides?.[fieldId]?.text_ids;
      const hasOverride = Array.isArray(overrideTokenIds) && overrideTokenIds.length > 0;
      const text_ids = hasOverride
        ? overrideTokenIds
        : getColumnTokenIds(effectiveColumn, c.key, effectivePage);

      const originalDetectedIds = Array.isArray(savedDraft?.original_detected?.text_ids) && savedDraft.original_detected.text_ids.length
        ? savedDraft.original_detected.text_ids
        : getColumnTokenIds(detectedColumn || effectiveColumn, c.key, effectivePage);
      const originalDetectedText = getTextFromIds(originalDetectedIds)
        || savedDraft?.original_detected?.text
        || ((Array.isArray(detectedColumn?.matchText) ? detectedColumn.matchText.join(" ") : detectedColumn?.matchText) || "—");

      const overrideDisplayText = hasOverride ? (getTextFromIds(overrideTokenIds) || null) : null;
      const currentDisplayText = overrideDisplayText
        || getTextFromIds(text_ids)
        || effectiveColumn?.text
        || (effectiveColumn ? ((Array.isArray(effectiveColumn?.matchText) ? effectiveColumn.matchText.join(" ") : effectiveColumn?.matchText) || "—") : (savedDraft?.text || "Not mapped"));

      // FIX: when user has drawn a new bbox (hasOverride), derive geometry from the
      // override tokens. Previously x_min/x_max/x_mid/bbox stayed at the stale
      // effectiveColumn values (savedGeometry || detectedColumn). Consequence:
      //   - sortedConfirmedZoneColumns read stale x_min/x_max → _sortX wrong
      //   - columnZones placed the stripe at the OLD x position
      //   - Sort order broke (e.g. Date stuck right of PO if originally
      //     mis-detected there) → separator handle constraints prevented
      //     dragging it back left
      //   - Drawing another column triggered a columnZones recompute, which
      //     re-rendered this column at its stale position ("snap-back")
      // The fallback in sortedConfirmedZoneColumns (use fieldOverrides if x_min
      // is NaN) didn't fire because x_min was DEFINED but stale.
      let overrideGeometry = null;
      if (hasOverride) {
        const overrideTokens = overrideTokenIds.map((id) => tokenMap[id]).filter(Boolean);
        if (overrideTokens.length > 0) {
          const xs = overrideTokens.flatMap((t) => [Number(t?.bbox?.x), Number(t?.bbox?.x2)]).filter(Number.isFinite);
          const ys = overrideTokens.flatMap((t) => [Number(t?.bbox?.y), Number(t?.bbox?.y2)]).filter(Number.isFinite);
          if (xs.length && ys.length) {
            const oxMin = Math.min(...xs);
            const oxMax = Math.max(...xs);
            overrideGeometry = {
              x_min: oxMin,
              x_max: oxMax,
              x_mid: (oxMin + oxMax) / 2,
              bbox:  {
                x:    oxMin,
                y:    Math.min(...ys),
                x2:   oxMax,
                y2:   Math.max(...ys),
                page: effectivePage,
              },
            };
          }
        }
      }

      return {
        id: fieldId,
        field_id: savedDraft?.field_id || fieldId,
        labelType: c.key,
        displayName: c.label,

        displayValue: currentDisplayText,
        original_displayValue: originalDetectedText,

        // FIX: mapped must reflect user-drawn overrides, not just effectiveColumn presence.
        // OLD: mapped: Boolean(effectiveColumn),
        mapped: Boolean(effectiveColumn || hasOverride),
        originalColumn: detectedColumn || effectiveColumn || null,
        suggestedBy: hasOverride
          ? "user-override"
          : savedGeometry
            ? "saved-draft"
            : claudeInvoiceMappings.some((item) => item?.labelType === c.key)
              ? "claude"
            : resolvedMappedColumns?.[c.key]
              ? "mapped-columns"
              : detectedColumn
                ? "table-headings"
                : "none",
        confidence: Number(savedDraft?.detected_confidence || effectiveColumn?.confidence || 0),
        selectionType: "column",
        column_index: Number.isFinite(Number(savedDraft?.column_index)) ? Number(savedDraft.column_index) : idx,
        text_ids: text_ids,

        original_text_ids: originalDetectedIds,
        x_min: overrideGeometry?.x_min ?? effectiveColumn?.x_min,
        x_mid: overrideGeometry?.x_mid ?? effectiveColumn?.x_mid,
        x_max: overrideGeometry?.x_max ?? effectiveColumn?.x_max,
        page: effectivePage,
        matchText: effectiveColumn?.matchText || savedDraft?.matchText,
        bbox: overrideGeometry?.bbox ?? effectiveColumn?.bbox,
      };
    });
  // FIX: added fieldOverrides to dep array so the memo recomputes when the user draws.
  // OLD: }, [invoiceColumnCards, resolvedTableHeadings, profile?.lines, layoutLines, tokenMap, sourceTokens, savedInvoiceDraftRows, tableHeaderYRange, sharedInvoiceHeaderAnchor, resolvedMappedColumns, claudeInvoiceMappings]);
  }, [invoiceColumnCards, resolvedTableHeadings, profile?.lines, layoutLines, tokenMap, sourceTokens, savedInvoiceDraftRows, tableHeaderYRange, sharedInvoiceHeaderAnchor, resolvedMappedColumns, claudeInvoiceMappings, fieldOverrides]);

  const fieldRegistry = useMemo(() => {
    return [...statementCards, ...invoiceMappings].reduce((acc, item) => {
      acc[item.id] = item;
      return acc;
    }, {});
  }, [statementCards, invoiceMappings]);

  // [ZONE MATCHING] All mapped, non-deleted invoice columns sorted left-to-right by x position.
  // Does not require validatedFields — zones render for any mapped column, not just confirmed ones.
  // FIX (fourth-color bug): user-drawn columns that have no auto-detected geometry have null x_min/x_max.
  // We fall back to fieldOverrides + tokenMap to derive x position from the drawn token bboxes.
  const sortedConfirmedZoneColumns = useMemo(() => {
    return invoiceMappings
      .filter((m) => m.mapped && !deletedFields?.[m.id])
      .map((m) => {
        let xMin = Number.isFinite(Number(m.x_min)) ? Number(m.x_min) : Number(m.bbox?.x ?? NaN);
        let xMax = Number.isFinite(Number(m.x_max)) ? Number(m.x_max) : Number(m.bbox?.x2 ?? NaN);
        if (!Number.isFinite(xMin) || !Number.isFinite(xMax)) {
          const overrideIds = fieldOverrides?.[m.id]?.text_ids;
          if (Array.isArray(overrideIds) && overrideIds.length > 0) {
            const toks = overrideIds.map((id) => tokenMap[id]).filter(Boolean);
            if (toks.length > 0) {
              xMin = Math.min(...toks.map((t) => Number(t?.bbox?.x ?? Infinity)));
              xMax = Math.max(...toks.map((t) => Number(t?.bbox?.x2 ?? -Infinity)));
            }
          }
        }
        const xMid = Number.isFinite(Number(m.x_mid)) ? Number(m.x_mid)
          : (Number.isFinite(xMin) && Number.isFinite(xMax) ? (xMin + xMax) / 2 : NaN);
        return { ...m, _sortX: xMid, _xMin: xMin, _xMax: xMax };
      })
      .filter((m) => Number.isFinite(m._sortX))
      .sort((a, b) => a._sortX - b._sortX);
  }, [invoiceMappings, deletedFields, fieldOverrides, tokenMap]);

  // [ZONE MATCHING] The page where the user's mapped invoice columns physically live.
  // Decouples zone calculation from currentPage — navigating to a no-table page (e.g.
  // a cover/summary page) cannot silently shrink allDetectedCols to empty.
  const invoiceTablePage = useMemo(() => {
    const pages = sortedConfirmedZoneColumns
      .map((m) => Number(m.page))
      .filter((p) => Number.isFinite(p) && p > 0);
    if (pages.length) {
      const freq = {};
      pages.forEach((p) => { freq[p] = (freq[p] || 0) + 1; });
      return Number(Object.entries(freq).sort((a, b) => b[1] - a[1])[0][0]);
    }
    const headings = layoutItems.filter((item) => item.__type === "table_headings");
    if (!headings.length) return null;
    const best = headings.reduce((acc, h) => {
      const n = Array.isArray(h?.columns) ? h.columns.length : 0;
      return n > (acc?.n ?? 0) ? { page: Number(h.page ?? 1), n } : acc;
    }, null);
    return best?.page ?? null;
  }, [sortedConfirmedZoneColumns, layoutItems]);

  // [ZONE MATCHING] table_headings entry used as the allDetectedCols source in columnZones.
  // Anchored to invoiceTablePage so gap detection sees the document's actual layout
  // regardless of currentPage; falls back to the richest table_headings entry if the
  // inferred page has no columns recorded.
  const tableHeadingsForZoneCalc = useMemo(() => {
    const all = layoutItems.filter((item) => item.__type === "table_headings");
    if (!all.length) return null;
    if (invoiceTablePage) {
      const found = all.find((item) => Number(item.page ?? 1) === invoiceTablePage);
      if (found && Array.isArray(found.columns) && found.columns.length > 0) return found;
    }
    return all.reduce((best, h) => {
      const n     = Array.isArray(h?.columns)    ? h.columns.length    : 0;
      const bestN = Array.isArray(best?.columns) ? best.columns.length : 0;
      return n > bestN ? h : best;
    }, null);
  }, [layoutItems, invoiceTablePage]);

  // [ZONE MATCHING] Zone boundaries [zoneLeft, zoneRight] per column in 0-1 page coordinates.
  // When an unconfirmed column (e.g. "Due Date") sits between two mapped columns, independent
  // right_${left} and left_${right} edges are computed so a transparent gap can be dragged over it.
  // When no gap column is detected, a single paired separator shares the same position for both edges.
  // Priority per edge: (1) active drag override, (2) saved zone_x_left/zone_x_right from payload,
  // (3) auto-computed default from heading token bboxes and gap column positions.
  // Source of allDetectedCols is tableHeadingsForZoneCalc (anchored to invoiceTablePage), NOT
  // resolvedTableHeadings (currentPage-scoped) — keeps zones stable across page navigation.
  const columnZones = useMemo(() => {
    if (!sortedConfirmedZoneColumns.length) return [];
    const n = sortedConfirmedZoneColumns.length;

    // All detected columns (confirmed + unconfirmed) from layout, sorted by center x
    const allDetectedCols = (Array.isArray(tableHeadingsForZoneCalc?.columns) ? tableHeadingsForZoneCalc.columns : [])
      .map((c) => ({
        _cx:  (Number(c.x_min) + Number(c.x_max)) / 2,
        _xm:  Number.isFinite(Number(c.x_min)) ? Number(c.x_min) : NaN,
        _xmx: Number.isFinite(Number(c.x_max)) ? Number(c.x_max) : NaN,
      }))
      .filter((c) => Number.isFinite(c._cx))
      .sort((a, b) => a._cx - b._cx);

    const leftEdges  = {};
    const rightEdges = {};

    // Outer edges: default tight to the heading token bbox rather than page edges 0/1,
    // so columns don't overclaim unrelated columns to the left/right by default.
    const firstCol = sortedConfirmedZoneColumns[0];
    const lastCol  = sortedConfirmedZoneColumns[n - 1];
    const firstLeftKey = `left_${firstCol.labelType}`;
    const lastRightKey = `right_${lastCol.labelType}`;

    leftEdges[firstCol.labelType] = separatorOverrides[firstLeftKey] !== undefined
      ? separatorOverrides[firstLeftKey]
      : (() => {
          const saved = savedInvoiceDraftRows.find((r) => r.labelType === firstCol.labelType);
          if (saved?.zone_x_left !== undefined && Number.isFinite(Number(saved.zone_x_left)))
            return Number(saved.zone_x_left);
          return Math.max(0, Number.isFinite(firstCol._xMin) ? firstCol._xMin - 0.02 : 0);
        })();

    rightEdges[lastCol.labelType] = separatorOverrides[lastRightKey] !== undefined
      ? separatorOverrides[lastRightKey]
      : (() => {
          const saved = savedInvoiceDraftRows.find((r) => r.labelType === lastCol.labelType);
          if (saved?.zone_x_right !== undefined && Number.isFinite(Number(saved.zone_x_right)))
            return Number(saved.zone_x_right);
          return Math.min(1, Number.isFinite(lastCol._xMax) ? lastCol._xMax + 0.02 : 1);
        })();

    for (let i = 0; i < n - 1; i++) {
      const col   = sortedConfirmedZoneColumns[i];
      const right = sortedConfirmedZoneColumns[i + 1];
      const colX2   = Number.isFinite(col._xMax)   ? col._xMax   : col._sortX;
      const rightX1 = Number.isFinite(right._xMin) ? right._xMin : right._sortX;

      // Gap cols: detected columns strictly between the two mapped columns,
      // not matching any mapped column within 0.03 x-tolerance
      const gapCols = allDetectedCols.filter((d) =>
        d._cx > col._sortX + 0.01 &&
        d._cx < right._sortX - 0.01 &&
        !sortedConfirmedZoneColumns.some((m) => Math.abs(m._sortX - d._cx) < 0.03)
      );

      if (gapCols.length > 0) {
        // Independent edges — transparent gap over the unconfirmed column(s)
        const nearestGap  = gapCols[0];
        const furthestGap = gapCols[gapCols.length - 1];
        const gapX1 = Number.isFinite(nearestGap._xm)   ? nearestGap._xm   : nearestGap._cx;
        const gapX2 = Number.isFinite(furthestGap._xmx) ? furthestGap._xmx : furthestGap._cx;

        const rightKey = `right_${col.labelType}`;
        const leftKey  = `left_${right.labelType}`;

        rightEdges[col.labelType] = separatorOverrides[rightKey] !== undefined
          ? separatorOverrides[rightKey]
          : (() => {
              const saved = savedInvoiceDraftRows.find((r) => r.labelType === col.labelType);
              if (saved?.zone_x_right !== undefined && Number.isFinite(Number(saved.zone_x_right)))
                return Number(saved.zone_x_right);
              return (colX2 + gapX1) / 2;
            })();

        leftEdges[right.labelType] = separatorOverrides[leftKey] !== undefined
          ? separatorOverrides[leftKey]
          : (() => {
              const saved = savedInvoiceDraftRows.find((r) => r.labelType === right.labelType);
              if (saved?.zone_x_left !== undefined && Number.isFinite(Number(saved.zone_x_left)))
                return Number(saved.zone_x_left);
              return (gapX2 + rightX1) / 2;
            })();
      } else {
        // Paired separator — single shared boundary; right_ key is canonical
        const rightKey = `right_${col.labelType}`;
        const sepX = separatorOverrides[rightKey] !== undefined
          ? separatorOverrides[rightKey]
          : (() => {
              const saved = savedInvoiceDraftRows.find((r) => r.labelType === col.labelType);
              if (saved?.zone_x_right !== undefined && Number.isFinite(Number(saved.zone_x_right)))
                return Number(saved.zone_x_right);
              return (colX2 + rightX1) / 2;
            })();
        rightEdges[col.labelType]  = sepX;
        leftEdges[right.labelType] = sepX;
      }
    }

    return sortedConfirmedZoneColumns.map((col) => ({
      ...col,
      zoneLeft:  leftEdges[col.labelType]  ?? 0,
      zoneRight: rightEdges[col.labelType] ?? 1,
    }));
  }, [sortedConfirmedZoneColumns, separatorOverrides, savedInvoiceDraftRows, tableHeadingsForZoneCalc]);

  // [ZONE MATCHING] Separator handle positions — outer handles on first/last column edges plus
  // inner handles between columns (paired when adjacent, independent when a gap column exists).
  // Each entry carries hint (tooltip text), pairedKey (null = independent), and drag constraints.
  const separatorPositions = useMemo(() => {
    if (!columnZones.length) return [];
    const result = [];
    const first = columnZones[0];
    const last  = columnZones[columnZones.length - 1];

    // Outer left handle — constrains first column from sweeping unrelated columns to its left
    result.push({
      key:       `left_${first.labelType}`,
      pairedKey: null,
      x:         first.zoneLeft,
      minX:      0,
      maxX:      first.zoneRight - 0.01,
      hint:      `left edge of ${ZONE_LABEL[first.labelType] ?? first.labelType}`,
    });

    // Inner handles between adjacent column pairs
    for (let i = 0; i < columnZones.length - 1; i++) {
      const col   = columnZones[i];
      const right = columnZones[i + 1];
      const hasGap = col.zoneRight < right.zoneLeft - 0.001;

      if (hasGap) {
        result.push({
          key:       `right_${col.labelType}`,
          pairedKey: null,
          x:         col.zoneRight,
          minX:      col.zoneLeft + 0.01,
          maxX:      right.zoneLeft - 0.01,
          hint:      `right edge of ${ZONE_LABEL[col.labelType] ?? col.labelType}`,
        });
        result.push({
          key:       `left_${right.labelType}`,
          pairedKey: null,
          x:         right.zoneLeft,
          minX:      col.zoneRight + 0.01,
          maxX:      right.zoneRight - 0.01,
          hint:      `left edge of ${ZONE_LABEL[right.labelType] ?? right.labelType}`,
        });
      } else {
        result.push({
          key:       `right_${col.labelType}`,
          pairedKey: `left_${right.labelType}`,
          x:         col.zoneRight,
          minX:      col.zoneLeft + 0.01,
          maxX:      right.zoneRight - 0.01,
          hint:      `boundary between ${ZONE_LABEL[col.labelType] ?? col.labelType} and ${ZONE_LABEL[right.labelType] ?? right.labelType}`,
        });
      }
    }

    // Outer right handle — constrains last column from sweeping unrelated columns to its right
    result.push({
      key:       `right_${last.labelType}`,
      pairedKey: null,
      x:         last.zoneRight,
      minX:      last.zoneLeft + 0.01,
      maxX:      1,
      hint:      `right edge of ${ZONE_LABEL[last.labelType] ?? last.labelType}`,
    });

    return result;
  }, [columnZones]);

  // [ZONE MATCHING] Stripe y-range in 0-1 normalized coords.
  // Top    = bottom edge of confirmed header row.
  // Bottom = max y2 of DocAI StatementBodyCell entities on this page (trust
  //          the model's body classification — same principle as v27 backend).
  //          Falls back to FOOTER_PATTERNS-based detection from raw tokens
  //          only when the page emits zero body cell entities.
  // Why not FOOTER_PATTERNS first: the regex matches `\bbalance\b` etc.,
  // which fires on legitimate body lines like "BALANCE FORWARD" and clips
  // the stripe to the first row.
  const { stripeTopNorm, stripeBottomNorm } = useMemo(() => {
    const top = tableHeaderYRange.max;

    // Primary: DocAI StatementBodyCell entities on this page.
    // Labels are 1-indexed at this point — extract_labels adds 1 to DocAI's
    // 0-indexed pageRefs.page, and that 1-indexed value flows through
    // map_labels_to_token_ids → profile.labels → getonboard.sz unchanged.
    const bodyCellLabels = (layoutLabels || []).filter((l) => {
      if (l.label !== "StatementBodyCell") return false;
      const lp = parseInt(String(l.page ?? l.bbox?.page ?? "1"), 10);
      return lp === pageNo;
    });

    if (bodyCellLabels.length > 0) {
      const maxY2 = Math.max(
        ...bodyCellLabels.map((l) => Number(l.bbox?.y2 ?? l.bbox?.y ?? 0))
      );
      return {
        stripeTopNorm:    top,
        stripeBottomNorm: Math.min(1, maxY2 + 0.005),
      };
    }

    // Fallback: no body cell entities on this page — use raw-token footer detection.
    const colXMin = sortedConfirmedZoneColumns.length > 0
      ? Math.min(...sortedConfirmedZoneColumns.map((c) => Number.isFinite(c._xMin) ? c._xMin : c._sortX)) - 0.02
      : 0;
    const colXMax = sortedConfirmedZoneColumns.length > 0
      ? Math.max(...sortedConfirmedZoneColumns.map((c) => Number.isFinite(c._xMax) ? c._xMax : c._sortX)) + 0.02
      : 1;

    const belowHeader = sourceTokens.filter((t) => {
      const tPage = Number(t?.page || 1);
      const tY    = Number(t?.bbox?.y);
      return tPage === pageNo && Number.isFinite(tY) && tY > tableHeaderYRange.max;
    });

    const footerToken = belowHeader
      .slice()
      .sort((a, b) => Number(a?.bbox?.y) - Number(b?.bbox?.y))
      .find((t) => FOOTER_PATTERNS.test(String(t?.text || t?.value || t?.word || "")));
    const footerY = footerToken ? Number(footerToken.bbox?.y) : Infinity;

    const bodyTokens = belowHeader.filter((t) => {
      const tY  = Number(t?.bbox?.y);
      const tCx = (Number(t?.bbox?.x) + Number(t?.bbox?.x2)) / 2;
      return tY < footerY
        && Number.isFinite(tCx) && tCx >= colXMin && tCx <= colXMax;
    });

    const bottom = bodyTokens.length > 0
      ? Math.min(1, Math.max(...bodyTokens.map((t) => Number(t?.bbox?.y2 ?? t?.bbox?.y))) + 0.005)
      : Math.min(1, tableHeaderYRange.max + 0.30);

    return { stripeTopNorm: top, stripeBottomNorm: bottom };
  }, [sourceTokens, sortedConfirmedZoneColumns, tableHeaderYRange, pageNo, layoutLabels]);

  // [ZONE MATCHING] Reset in-memory separator overrides whenever the set of mapped label types
  // changes (user adds or removes a column), so stale overrides for the old arrangement are cleared.
  const _confirmedZoneKey = sortedConfirmedZoneColumns.map((c) => c.labelType).sort().join(",");
  useEffect(() => {
    setSeparatorOverrides({});
  }, [_confirmedZoneKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const getFieldByName = (fieldName) => {
    return fieldRegistry[fieldName] || null;
  };

  const getBoxesFromField = (field) => {
    if (!field) return [];
    const overrideIds = field?.id ? fieldOverrides?.[field.id]?.text_ids : null;

    const toBox = (token) => {
      const box = { x: token?.bbox?.x, y: token?.bbox?.y, x2: token?.bbox?.x2, y2: token?.bbox?.y2, page: token?.page };
      if (Number.isFinite(box.x) && Number.isFinite(box.y) && Number.isFinite(box.x2) && Number.isFinite(box.y2)) return box;
      return null;
    };




    if (Array.isArray(overrideIds) && overrideIds.length > 0 && field?.selectionType !== "column") {
      const overridePage = Number(field?.page || 1);
      const mergedBoxes = overrideIds
        .map((id) => tokenMap[id])
        .filter(Boolean)
        .filter((token) => Number(token?.page || 1) === overridePage)
        .map((token) => toBox(token))
        .filter(Boolean);
      const merged = mergeBoxes(mergedBoxes);
      return merged ? [merged] : [];
    }

    if (field?.selectionType !== "column") {
      const backendBox = normalizeBbox(field?.bbox);
      if (backendBox) {
        return [{
          ...backendBox,
          page: Number(field?.page || backendBox.page || 1),
        }];
      }
    }

    if (
      Array.isArray(field?.text_ids) &&
      field.text_ids.length > 0 &&
      field?.selectionType !== "column"
    ) {
      const fieldPage = Number(field?.page || 1);
      const mergedBoxes = field.text_ids
        .map((id) => tokenMap[id])
        .filter(Boolean)
        .filter((token) => Number(token?.page || 1) === fieldPage)
        .map((token) => toBox(token))
        .filter(Boolean);

      const merged = mergeBoxes(mergedBoxes);
      return merged ? [merged] : [];
    }


    // FIX (Bug B): column override check comes FIRST — a user draw must always beat stored text_ids.
    // invoiceMappings (fieldRegistry source) doesn't depend on fieldOverrides, so field.text_ids
    // always contains the original computed geometry. Without this ordering, the text_ids block
    // below fires first and returns the stale column (e.g. Due Date) ignoring the drawn override.
    // OLD order was: text_ids block → override check (override unreachable when text_ids non-empty)
    if (field?.selectionType === "column" && Array.isArray(overrideIds) && overrideIds.length > 0) {
      const fieldPage = Number(field?.page || 1);
      const overrideBoxes = overrideIds
        .map((id) => tokenMap[id])
        .filter(Boolean)
        .filter((token) => Number(token?.page || 1) === fieldPage)
        .map((token) => toBox(token))
        .filter(Boolean);
      const overrideMerged = mergeBoxes(overrideBoxes);
      if (overrideMerged) return [{ ...overrideMerged, page: fieldPage }];
    }

    // Column text_ids — use stored geometry when no user draw override exists.
    // OLD (broken placement): override check was inside this block after the early return,
    //   so it could never fire when text_ids were non-empty.
    if (
      field?.selectionType === "column" &&
      Array.isArray(field?.text_ids) &&
      field.text_ids.length > 0
    ) {
      const fieldPage = Number(field?.page || 1);
      const mergedBoxes = field.text_ids
        .map((id) => tokenMap[id])
        .filter(Boolean)
        .filter((token) => Number(token?.page || 1) === fieldPage)
        .map((token) => toBox(token))
        .filter(Boolean);

      const merged = mergeBoxes(mergedBoxes);
      if (merged) return [merged];
    }

    if (field?.selectionType === "column" && Number.isFinite(field?.x_min) && Number.isFinite(field?.x_max)) {
      const page = Number(field?.page || 1);

      // When user manually redraws a column heading, use that explicit token selection as-is.
      // Do not constrain it back to the original column geometry.
      if (Array.isArray(overrideIds) && overrideIds.length > 0) {
        const overrideBoxes = overrideIds
          .map((id) => tokenMap[id])
          .filter(Boolean)
          .map((token) => toBox(token))
          .filter(Boolean);
        const overrideMerged = mergeBoxes(overrideBoxes);
        if (overrideMerged) {
          return [{
            ...overrideMerged,
            page,
          }];
        }
      }

      const tokens = layoutTokens.length ? layoutTokens : profile?.tokens || [];
      const sharedY =
        Number(sharedInvoiceHeaderAnchor?.page) === page && Number.isFinite(Number(sharedInvoiceHeaderAnchor?.y))
          ? Number(sharedInvoiceHeaderAnchor.y)
          : null;
      const headerBandMin = Number.isFinite(sharedY) ? Math.max(0, sharedY - 0.012) : Math.max(0, Number(tableHeaderYRange.min));
      const headerBandMax = Number.isFinite(sharedY) ? Math.min(1, sharedY + 0.018) : Math.min(1, Number(tableHeaderYRange.max));

      const providedTokenIds = Array.isArray(overrideIds)
        ? overrideIds
        : (Array.isArray(field?.text_ids) ? field.text_ids : []);

      const providedTokens = providedTokenIds
        .map((id) => tokenMap[id])
        .filter(Boolean);
      const preferredTokens = getColumnHeaderTokens(field, providedTokens, page);
      const scannedTokens = getColumnHeaderTokens(field, tokens, page);
      const columnTokens = preferredTokens.length ? preferredTokens : scannedTokens;

      const merged = mergeBoxes(columnTokens.map((token) => toBox(token)).filter(Boolean));
      if (merged) {
        return [{
          ...merged,
          page,
        }];
      }

      const headerBox = {
        x: Math.max(0, Number(field.x_min)),
        y: headerBandMin,
        x2: Math.min(1, Number(field.x_max)),
        y2: headerBandMax,
        page,
      };

      if (
        Number.isFinite(headerBox.x) &&
        Number.isFinite(headerBox.y) &&
        Number.isFinite(headerBox.x2) &&
        Number.isFinite(headerBox.y2) &&
        headerBox.x2 > headerBox.x &&
        headerBox.y2 > headerBox.y
      ) {
        return [headerBox];
      }

      return [];
    }

    const ids = [];
    if (field?.text_id) ids.push(field.text_id);
    if (Array.isArray(field?.text_ids)) ids.push(...field.text_ids);
    if (Array.isArray(field?.text_id)) ids.push(...field.text_id);

    const selectedToken = ids.map((id) => tokenMap[id]).find(Boolean) || null;
    const selectedTokenBox = selectedToken ? toBox(selectedToken) : null;
    return selectedTokenBox ? [selectedTokenBox] : [];
  };

  const handleSelect = (field) => {
    return getBoxesFromField(field);
  };

  const handleFieldClick = (field, fieldName) => {
    setActiveField(fieldName);
    const fieldPage = Number(field?.page || field?.bbox?.page || 1);
    if (fieldPage !== Number(currentPage)) {
      handlePageChange(fieldPage);
    }
    handleSelect(field);
  };

  const handleOverlayBoxClick = (fieldId) => {
    if (drawFieldId) return;
    if (!fieldId) return;
    const field = getFieldByName(fieldId);
    if (!field) return;
    setActiveField(fieldId);
  };

  const autoActiveBoxes = useMemo(() => {
    if (!activeField) return [];
    if (deletedFields?.[activeField]) return [];
    const field = getFieldByName(activeField);
    if (!field) return [];
    const selected = handleSelect(field);
    if (field?.selectionType === "column" && selected.length > 0) {
      const header = selected[0];
      return [{
        type: `active-header-${activeField}`,
        text_id: `${activeField}-header`,
        fieldId: activeField,
        color: HIGHLIGHT_COLORS.invoiceHeaderActive,
        bbox: header,
        page: header?.page,
      }];
    }

    return selected.map((bbox, idx) => ({
      type: `active-${activeField}`,
      text_id: field?.text_id || `${activeField}-${idx}`,
      fieldId: activeField,
      color: "#0d6efd",
      bbox,
      page: bbox?.page,
    }));
  // FIX (Bug C): added fieldOverrides — without it, drawing on an already-active field
  //              left the memo stale (setActiveField was a no-op so memo never re-ran).
  // OLD: }, [activeField, fieldRegistry, deletedFields]);
  }, [activeField, fieldRegistry, deletedFields, fieldOverrides]);

  const headerTabBoxes = useMemo(() => {
    const activeIsInvoiceHeading = invoiceMappings.some((item) => item?.id === activeField);

    const statementBoxes = statementCards
      .filter((item) => !deletedFields?.[item?.id])
      .filter((item) => item?.id !== activeField)
      .flatMap((item) =>
        handleSelect(item).map((bbox, idx) => ({
          type: `header-${item.id}`,
          text_id: item?.text_id || `${item.id}-${idx}`,
          fieldId: item.id,
          color: HIGHLIGHT_COLORS.headerGroup,
          bbox,
          page: bbox?.page,
        }))
      );

    const invoiceBoxes = activeIsInvoiceHeading ? [] : invoiceMappings
      .filter((item) => item?.mapped && item?.id !== activeField && !deletedFields?.[item?.id])
      .flatMap((item) =>
        handleSelect(item).map((bbox, idx) => ({
          type: `header-${item.id}`,
          text_id: `${item.id}-${idx}`,
          fieldId: item.id,
          color: HIGHLIGHT_COLORS.headerGroup,
          bbox,
          page: bbox?.page,
        }))
      );

    return [...statementBoxes, ...invoiceBoxes];
  }, [statementCards, invoiceMappings, activeField, tokenMap, profile, deletedFields]);

  const displayBoxes = useMemo(() => {
    return [...headerTabBoxes, ...autoActiveBoxes];
  }, [headerTabBoxes, autoActiveBoxes]);

  const statementMandatoryStatus = useMemo(() => {
    const byLabel = statementCards.reduce((acc, item) => {
      const isConfirmed = Boolean(validatedFields?.[item.id] && !deletedFields?.[item.id]);
      const key = item?.label;
      if (!key) return acc;
      acc[key] = Boolean(acc[key]) || isConfirmed;
      return acc;
    }, {});

    const parentOrTradingSatisfied = MANDATORY_STATEMENT_ANY_OF.some((label) => Boolean(byLabel[label]));
    const requiredAllSatisfied = MANDATORY_STATEMENT_ALL.every((label) => Boolean(byLabel[label]));
    const allRequiredSatisfied = parentOrTradingSatisfied && requiredAllSatisfied;

    const missing = [];
    if (!parentOrTradingSatisfied) missing.push("Supplier Parent Name or Franchise Name");
    MANDATORY_STATEMENT_ALL.forEach((label) => {
      if (!byLabel[label]) missing.push(STATEMENT_LABEL_DISPLAY[label] || label);
    });

    return { byLabel, parentOrTradingSatisfied, requiredAllSatisfied, allRequiredSatisfied, missing };
  }, [statementCards, validatedFields, deletedFields]);

  const invoiceMandatoryStatus = useMemo(() => {
    const byType = invoiceMappings.reduce((acc, item) => {
      const isConfirmed = Boolean(validatedFields?.[item.id] && !deletedFields?.[item.id]);
      const key = item?.labelType;
      if (!key) return acc;
      acc[key] = Boolean(acc[key]) || isConfirmed;
      return acc;
    }, {});

    const allRequiredSatisfied = MANDATORY_INVOICE_COLUMNS.every((type) => Boolean(byType[type]));
    const missing = MANDATORY_INVOICE_COLUMNS.filter((type) => !byType[type]).map((type) => INVOICE_COLUMN_DISPLAY[type] || type);

    return { byType, allRequiredSatisfied, missing };
  }, [invoiceMappings, validatedFields, deletedFields]);

  const confirmedConfigPayload = useMemo(() => {
    const serializeStatementLabel = (item) => {
      const currentTextIds = fieldOverrides?.[item.id]?.text_ids || item?.text_ids || (item?.text_id ? [item.text_id] : []);
      const currentBoxes = getBoxesFromField(item);
      const currentBbox = normalizeBbox(currentBoxes?.[0] || item?.bbox);
      const originalTextIds = Array.isArray(item?.original_text_ids)
        ? item.original_text_ids
        : item?.text_id
          ? [item.text_id]
          : [];
      const originalBoxes = getBoxesFromTokenIds(originalTextIds);
      const originalBbox = normalizeBbox(originalBoxes?.[0] || item?.bbox);
      const currentText = getTextFromIds(currentTextIds) || item?.displayValue || item?.text || "";
      const originalText = getTextFromIds(originalTextIds) || item?.original_displayValue || item?.text || "";
      const edited = Boolean(fieldOverrides?.[item.id]?.text_ids?.length);

      return {
        field_id: item?.field_id || item?.id,
        page: Number(item?.page || currentBbox?.page || originalBbox?.page || 1),
        label: item?.label || item?.displayName,
        text: currentText,
        text_ids: currentTextIds,
        bbox: currentBbox,
        source: "user_confirmed",
        confidence: 1,
        confirmed: true,
        edited,
        ...(edited || originalTextIds.length || originalText
          ? {
              original_detected: {
                text: originalText,
                text_ids: originalTextIds,
                bbox: originalBbox,
              },
            }
          : {}),
      };
    };

    const serializeInvoiceColumn = (item, idx) => {
      const currentTextIds = fieldOverrides?.[item.id]?.text_ids || item?.text_ids || [];
      const currentBoxes = getBoxesFromField(item);
      const currentBbox = normalizeBbox(currentBoxes?.[0]);
      const originalTextIds = Array.isArray(item?.original_text_ids) ? item.original_text_ids : [];
      const originalBoxes = getBoxesFromTokenIds(originalTextIds);
      const originalBbox = normalizeBbox(originalBoxes?.[0]);
      const currentText = getTextFromIds(currentTextIds) || item?.displayValue || "";
      const originalText = getTextFromIds(originalTextIds) || item?.original_displayValue || item?.displayValue || "";
      const detectedConfidence = Number(item?.confidence || item?.originalColumn?.confidence || item?.originalColumn?.score || 0);
      const edited = Boolean(fieldOverrides?.[item.id]?.text_ids?.length);

      const bboxXMin = Number(currentBbox?.x);
      const bboxXMax = Number(currentBbox?.x2);
      const bboxPage = Number(currentBbox?.page);
      const itemXMin = Number(item?.x_min);
      const itemXMax = Number(item?.x_max);
      const itemXMid = Number(item?.x_mid);

      const resolvedXMin = Number.isFinite(bboxXMin) ? bboxXMin : itemXMin;
      const resolvedXMax = Number.isFinite(bboxXMax) ? bboxXMax : itemXMax;
      const resolvedXMid = Number.isFinite(bboxXMin) && Number.isFinite(bboxXMax)
        ? (bboxXMin + bboxXMax) / 2
        : (Number.isFinite(itemXMid)
            ? itemXMid
            : (itemXMin + itemXMax) / 2);
      const resolvedPage = Number.isFinite(bboxPage)
        ? bboxPage
        : Number(item?.page || resolvedTableHeadings?.page || 1);
      const resolvedMatchText = currentText
        ? [currentText]
        : (Array.isArray(item?.matchText)
            ? item.matchText
            : (item?.matchText ? [item.matchText] : []));

      // [ZONE MATCHING] Look up the current zone boundary for this column so zone_x_left/zone_x_right
      // travel in the payload. The stored proc reads these when present and falls back to gap-midpoint
      // auto-computation when absent (e.g. columns published before zone UI was added).
      const colZone = columnZones.find((z) => z.labelType === item.labelType);

      return {
        field_id: item?.field_id || item?.id,
        column_index: Number.isFinite(Number(item?.column_index)) ? Number(item.column_index) : idx,
        page: resolvedPage,
        x_max: resolvedXMax,
        x_mid: resolvedXMid,
        x_min: resolvedXMin,
        labelType: item?.labelType,
        matchText: resolvedMatchText,
        text: currentText,
        text_ids: currentTextIds,
        bbox: currentBbox,
        confidence: 1,
        detected_confidence: detectedConfidence,
        confirmed_confidence: 1,
        source: "user_confirmed",
        confirmed: true,
        edited,
        // [ZONE MATCHING] Zone boundary fields — page-normalized (0-1). Stored proc converts to
        // table-relative normalized coords (x_left_norm/x_right_norm) via anchor+width division.
        ...(Number.isFinite(colZone?.zoneLeft)  ? { zone_x_left:  colZone.zoneLeft  } : {}),
        ...(Number.isFinite(colZone?.zoneRight) ? { zone_x_right: colZone.zoneRight } : {}),
        ...(edited || originalTextIds.length || originalText
          ? {
              original_detected: {
                text: originalText,
                text_ids: originalTextIds,
                bbox: originalBbox,
              },
            }
          : {}),
      };
    };

    const confirmedStatementLabels = statementCards
      .reduce((acc, item) => {
        if (!validatedFields?.[item.id] || deletedFields?.[item.id]) return acc;
        acc.push(serializeStatementLabel(item));
        return acc;
      }, []);

    const confirmedInvoiceColumns = invoiceMappings
      .reduce((acc, item, index) => {
        if (!validatedFields?.[item.id] || deletedFields?.[item.id] || !item?.mapped) return acc;
        acc.push(serializeInvoiceColumn(item, index));
        return acc;
      }, []);

    const ignoredStatementLabels = statementCards
      .reduce((acc, item) => {
        if (!deletedFields?.[item.id]) return acc;
        acc.push({
          ...serializeStatementLabel(item),
          source: "user_ignored",
          confirmed: false,
          ignored: true,
        });
        return acc;
      }, []);

    const ignoredInvoiceColumns = invoiceMappings
      .reduce((acc, item, index) => {
        if (!deletedFields?.[item.id]) return acc;
        acc.push({
          ...serializeInvoiceColumn(item, index),
          source: "user_ignored",
          confirmed: false,
          ignored: true,
        });
        return acc;
      }, []);

    const normalizedSupplierResolution = normalizeSupplierResolutionForSave(supplierResolutionDraft);
    const existingConfigMeta = savedConfirmedLabels?.config_meta || rootPayload?.confirmed_labels?.config_meta || {};
    const configMeta = {
      document_type:
        existingConfigMeta?.document_type ||
        rootPayload?.document_type ||
        profile?.document_type ||
        profile?.doc_type ||
        docObj?.doc_type ||
        "invoice_statement",
      canonical_name:
        existingConfigMeta?.canonical_name ||
        rootPayload?.canonical_name ||
        profile?.canonical_name ||
        fileName ||
        "",
      supplier_id:
        existingConfigMeta?.supplier_id ||
        normalizedSupplierResolution?.canonical_supplier?.supplier_id ||
        rootPayload?.supplier_id ||
        null,
      version: Number(existingConfigMeta?.version || savedConfirmedLabels?.version || rootPayload?.confirmed_labels?.version || 1),
    };

    return {
      onboarding: {
        labels: confirmedStatementLabels,
        tableHeadings: confirmedInvoiceColumns.length
          ? [
              {
                page: Number(resolvedTableHeadings?.page || 1),
                columns: confirmedInvoiceColumns,
              },
            ]
          : [],
        ignored_labels: ignoredStatementLabels,
        ignored_columns: ignoredInvoiceColumns,
        meta: {
          status: "draft",
          supplierResolution: normalizedSupplierResolution,
        },
        config_meta: configMeta,
      },
    };
  // [ZONE MATCHING] columnZones added so zone_x_left/zone_x_right in serializeInvoiceColumn
  // recompute whenever the user adjusts separator positions or saved zone data changes.
  }, [statementCards, invoiceMappings, validatedFields, deletedFields, fieldOverrides, resolvedTableHeadings, tokenMap, supplierResolutionDraft, savedConfirmedLabels, rootPayload, profile, docObj, fileName, columnZones, separatorOverrides]);

  const getSupplierRecordName = (record) => {
    return String(record?.name || record?.supplier_name || "").trim();
  };

  const getSupplierRecordIds = (record) => {
    const normalizeId = (value) => {
      if (value == null) return null;
      if (typeof value === "number") return Number.isFinite(value) ? value : null;
      const text = String(value).trim();
      if (!text) return null;
      return /^\d+$/.test(text) ? Number(text) : text;
    };

    const pickFirstId = (values) => {
      for (const value of values) {
        const normalized = normalizeId(value);
        if (normalized != null) return normalized;
      }
      return null;
    };

    if (!record || typeof record !== "object") {
      return {
        supplier_id: null,
        alias_id: null,
      };
    }

    return {
      // Search payloads can expose supplier ids under different key names.
      supplier_id: pickFirstId([
        record?.supplier_id,
        record?.master_supplier_id,
        record?.trading_supplier_id,
        record?.parent_supplier_id,
        record?.dpa_master_supplier_id,
        record?.supplierId,
        record?.masterSupplierId,
        record?.supplier?.supplier_id,
        record?.supplier?.id,
        record?.id,
      ]),
      // Alias id should only come from explicit alias fields.
      alias_id: pickFirstId([
        record?.alias_id,
        record?.supplier_alias_id,
        record?.trading_supplier_alias_id,
        record?.parent_supplier_alias_id,
        record?.aliasId,
      ]),
    };
  };

  const normalizeSupplierSelectionBlock = (selection) => {
    if (!selection || typeof selection !== "object") return null;
    const selectedIds = selection?.selected_ids && typeof selection.selected_ids === "object"
      ? {
          supplier_id: selection.selected_ids?.supplier_id ?? null,
          alias_id: selection.selected_ids?.alias_id ?? null,
        }
      : {
          supplier_id: null,
          alias_id: null,
        };
    const draftAdd = selection?.draft_add && typeof selection.draft_add === "object"
      ? { ...selection.draft_add }
      : null;
    const searchText = String(selection?.search_text || "");
    const selectedName = String(selection?.selected_name || "").trim();
    const mode = selection?.mode === "existing" || selection?.mode === "draft_add"
      ? selection.mode
      : null;

    if (!mode && !searchText && !selectedName && !draftAdd) return null;

    return {
      mode,
      search_text: searchText,
      selected_name: selectedName,
      selected_ids: selectedIds,
      draft_add: draftAdd,
      observed_name: String(selection?.observed_name || selectedName || searchText || "").trim(),
      create_alias: Boolean(selection?.create_alias),
    };
  };

  const normalizeSavedSupplierSelectionEntry = (selection) => {
    const selectionObj = selection && typeof selection === "object" ? selection : {};
    const selectedIds = selectionObj?.selected_ids && typeof selectionObj.selected_ids === "object"
      ? {
          supplier_id: selectionObj.selected_ids?.supplier_id ?? null,
          alias_id: selectionObj.selected_ids?.alias_id ?? null,
        }
      : { supplier_id: null, alias_id: null };

    const draftAddRaw = selectionObj?.draft_add && typeof selectionObj.draft_add === "object"
      ? selectionObj.draft_add
      : null;
    const draftAdd = draftAddRaw
      ? {
          name: String(draftAddRaw?.name || ""),
          street: String(draftAddRaw?.street || ""),
          city: String(draftAddRaw?.city || ""),
          province: String(draftAddRaw?.province || ""),
          postal_code: String(draftAddRaw?.postal_code || ""),
          country: String(draftAddRaw?.country || ""),
        }
      : null;

    const selectedName = String(selectionObj?.selected_name || "").trim();
    const searchText = String(selectionObj?.search_text ?? selectedName);
    let mode = selectionObj?.mode === "existing" || selectionObj?.mode === "draft_add"
      ? selectionObj.mode
      : null;

    if (!mode) {
      if (selectedIds?.supplier_id != null || selectedIds?.alias_id != null) {
        mode = "existing";
      } else if (draftAdd?.name || selectedName) {
        mode = "draft_add";
      }
    }

    return {
      mode,
      search_text: searchText,
      selected_name: selectedName,
      selected_ids: selectedIds,
      draft_add: draftAdd,
    };
  };

  const normalizeSavedSupplierSelection = (selection) => {
    const source = selection && typeof selection === "object" ? selection : {};
    return {
      parent: normalizeSavedSupplierSelectionEntry(source?.parent),
      trading: normalizeSavedSupplierSelectionEntry(source?.trading),
    };
  };

  const buildSupplierSelectionDraft = (kind) => {
    const selectedRecord = supplierSelectedRecords?.[kind];
    const savedDraftAdd = supplierAdditions?.[kind] && typeof supplierAdditions[kind] === "object"
      ? { ...supplierAdditions[kind] }
      : null;
    const unsavedOverlayDraft = showSupplierAddOverlay
      && supplierAddKind === kind
      && String(supplierAddDraft?.name || "").trim()
      ? { ...supplierAddDraft }
      : null;
    const draftAdd = savedDraftAdd || unsavedOverlayDraft;
    const selectedIdsFromRecord = getSupplierRecordIds(selectedRecord);
    const savedMeta = savedConfirmedLabels?.meta || {};
    const selectedIds = {
      supplier_id:
        selectedIdsFromRecord.supplier_id ??
        (kind === "parent" ? savedMeta.parent_supplier_id : savedMeta.trading_supplier_id) ??
        null,
      alias_id:
        selectedIdsFromRecord.alias_id ??
        (kind === "parent" ? savedMeta.parent_supplier_alias_id : savedMeta.trading_supplier_alias_id) ??
        null,
    };
    const searchText = String(supplierSelections?.[kind] ?? "");
    const selectedName = selectedRecord
      ? getSupplierRecordName(selectedRecord)
      : String(draftAdd?.name || searchText || (kind === "parent" ? supplierConfirmData.parentName : supplierConfirmData.tradingName) || "").trim();

    let mode = null;
    if (selectedIds.supplier_id != null || selectedIds.alias_id != null) {
      mode = "existing";
    } else if (draftAdd?.name) {
      mode = "draft_add";
    }

    if (!mode && !searchText && !selectedName && !draftAdd) {
      return null;
    }

    return normalizeSupplierSelectionBlock({
      mode,
      search_text: searchText,
      selected_name: selectedName,
      selected_ids: selectedIds,
      draft_add: draftAdd,
      observed_name: String(selectedName || searchText || "").trim(),
      create_alias:
         mode === "existing" &&
         selectedIds.supplier_id != null &&
         selectedName &&
         selectedName.toLowerCase() !== "not detected",
    });
  };

  const buildSupplierUpdate = (supplierSelection) => {
    const normalizeAddress = (value) => {
      if (!value || typeof value !== "object") return null;
      const street = String(value?.street || "").trim();
      const city = String(value?.city || "").trim();
      const province = String(value?.province || "").trim();
      const postal_code = String(value?.postal_code || "").trim();
      const country = String(value?.country || "").trim();
      if (!(street || city || province || postal_code || country)) return null;
      return { street, city, province, postal_code, country };
    };

    const getAddressByKind = (kind, selectionBlock) => {
      const selectedRecord = supplierSelectedRecords?.[kind];
      const fromSelected = normalizeAddress({
        street: selectedRecord?.street,
        city: selectedRecord?.city,
        province: selectedRecord?.province,
        postal_code: selectedRecord?.postal_code,
        country: selectedRecord?.country,
      });
      const fromDraftAdd = normalizeAddress(selectionBlock?.draft_add || {});
      if (fromSelected) return fromSelected;
      if (fromDraftAdd) return fromDraftAdd;
      if (kind === "parent") return normalizeAddress(effectiveSupplierResolution?.address || {});
      return null;
    };

    const parent = normalizeSupplierSelectionBlock(supplierSelection?.parent);
    const trading = normalizeSupplierSelectionBlock(supplierSelection?.trading);
    const hasExisting = parent?.mode === "existing" || trading?.mode === "existing";
    const hasDraftAdd = parent?.mode === "draft_add" || trading?.mode === "draft_add";

    return {
      parent_supplier_id: parent?.mode === "existing" ? (parent?.selected_ids?.supplier_id ?? null) : null,
      parent_supplier_alias_id: parent?.mode === "existing" ? (parent?.selected_ids?.alias_id ?? null) : null,
      trading_supplier_id: trading?.mode === "existing" ? (trading?.selected_ids?.supplier_id ?? null) : null,
      trading_supplier_alias_id: trading?.mode === "existing" ? (trading?.selected_ids?.alias_id ?? null) : null,
      parent_address: getAddressByKind("parent", parent),
      trading_address: getAddressByKind("trading", trading),
      supplier_resolution_status: hasExisting ? "resolved" : (hasDraftAdd ? "draft_add_pending" : "pending"),
      supplier_resolution_method: hasExisting ? "manual_existing" : (hasDraftAdd ? "manual_draft_add" : "manual"),
      supplier_confirmed_by: docObj?.uploadBy || null,
      supplier_confirmed_at: new Date().toISOString(),
    };
  };

  const buildOnboardingDraftPayload = (nextStep = activeStep, options = {}) => {
    const { includeSupplierSelection = false } = options;
    const onboarding = confirmedConfigPayload?.onboarding || {};
    const existingMeta = {
      ...(savedConfirmedLabels?.meta && typeof savedConfirmedLabels.meta === "object" ? savedConfirmedLabels.meta : {}),
      ...(onboarding?.meta && typeof onboarding.meta === "object" ? onboarding.meta : {}),
    };

    const fallbackConfirmed = {
      labels: Array.isArray(onboarding?.labels) ? onboarding.labels : [],
      tableHeadings: Array.isArray(onboarding?.tableHeadings) ? onboarding.tableHeadings : [],
      ignored_labels: Array.isArray(onboarding?.ignored_labels) ? onboarding.ignored_labels : [],
      ignored_columns: Array.isArray(onboarding?.ignored_columns) ? onboarding.ignored_columns : [],
      meta: {
        ...existingMeta,
        status: "draft",
      },
      config_meta: {
        ...(onboarding?.config_meta || {}),
      },
    };

    // Build the publish-oriented confirmed_labels object from current UI state.
    // This prevents supplier-step saves from re-sending stale, initially-loaded mappings.
    const confirmed_labels = {
      ...fallbackConfirmed,
    };

    // Build the UI/wizard state confirmation_draft object
    const confirmation_draft = {
      active_step: nextStep,
      validated_fields: { ...(validatedFields || {}) },
      deleted_fields: { ...(deletedFields || {}) },
      field_overrides: { ...(fieldOverrides || {}) },
    };

    if (includeSupplierSelection) {
      const supplierSelection = {
        parent: buildSupplierSelectionDraft("parent"),
        trading: buildSupplierSelectionDraft("trading"),
      };
      const existingSupplierMeta = savedConfirmedLabels?.meta && typeof savedConfirmedLabels.meta === "object"
        ? savedConfirmedLabels.meta
        : {};
      const supplier_update = buildSupplierUpdate(supplierSelection);
      let normalizedSupplierResolution = normalizeSupplierResolutionForSave(effectiveSupplierResolution || supplierResolutionDraft);

      // If the user confirmed a supplier address label, sync it into supplierResolution.address.
      // Match on label (semantic name) not field_id (generated slug like statement_label_xxx_pN_N).
      const allConfirmedLabels = Array.isArray(onboarding?.labels) ? onboarding.labels : [];
      const confirmedAddressLabel =
        allConfirmedLabels.find((l) => l?.label === "SupplierParentAddress") ||
        allConfirmedLabels.find((l) => l?.label === "SupplierTradingAddress") ||
        null;
      if (confirmedAddressLabel?.text) {
        const addressRaw = String(confirmedAddressLabel.text).trim();
        const parsed = parseCanadianAddress(addressRaw) || {};
        const existingAddr = normalizedSupplierResolution?.address || {};
        normalizedSupplierResolution = {
          ...(normalizedSupplierResolution || {}),
          address: {
            ...existingAddr,
            raw: addressRaw,
            street: parsed?.street || existingAddr?.street || "",
            city: parsed?.city || existingAddr?.city || "",
            province: parsed?.province || existingAddr?.province || "",
            postal_code: parsed?.postal_code || existingAddr?.postal_code || "",
            country: normalizeCountry(parsed?.country || existingAddr?.country || ""),
            address_source: "confirmed_label",
          },
        };
      }

      const payeeSlot = payeeGate.slot || (supplierSelection?.trading?.selected_ids?.supplier_id != null ? "trading" : "parent");
      const payeeIds = supplierSelection?.[payeeSlot]?.selected_ids || {};
      confirmation_draft.supplier_selection = {
        ...supplierSelection,
        payee: payeeIds?.supplier_id != null
          ? { master_id: payeeIds.supplier_id, alias_id: payeeIds.alias_id ?? null, slot: payeeSlot }
          : null,
        merge_requests: mergeRequests,
      };

      confirmed_labels.meta = {
        ...confirmed_labels.meta,
        supplierResolution: normalizedSupplierResolution || confirmed_labels?.meta?.supplierResolution || null,
        parent_supplier_id: supplier_update.parent_supplier_id ?? existingSupplierMeta?.parent_supplier_id ?? null,
        parent_supplier_alias_id:
          supplier_update.parent_supplier_alias_id
          ?? existingSupplierMeta?.parent_supplier_alias_id
          ?? existingSupplierMeta?.parent_alias_id
          ?? null,
        trading_supplier_id:
          supplier_update.trading_supplier_id
          ?? existingSupplierMeta?.trading_supplier_id
          ?? null,
        trading_supplier_alias_id:
          supplier_update.trading_supplier_alias_id
          ?? existingSupplierMeta?.trading_supplier_alias_id
          ?? existingSupplierMeta?.trading_alias_id
          ?? null,
        supplier_resolution_status: supplier_update.supplier_resolution_status || existingSupplierMeta?.supplier_resolution_status || null,
        supplier_resolution_method: supplier_update.supplier_resolution_method || existingSupplierMeta?.supplier_resolution_method || null,
        supplier_confirmed_by: supplier_update.supplier_confirmed_by || existingSupplierMeta?.supplier_confirmed_by || null,
        supplier_confirmed_at: supplier_update.supplier_confirmed_at || existingSupplierMeta?.supplier_confirmed_at || null,
      };
      return {
        confirmed_labels,
        confirmation_draft,
        supplier_update,
      };
    }

    return {
      confirmed_labels,
      confirmation_draft,
    };
  };

  const getOnboardingIdForSave = () => {
    return (
      rootPayload?.onboarding_id ||
      rootPayload?.id ||
      rootPayload?.document_onboarding_id ||
      invStatId ||
      null
    );
  };

  const supplierResolvedForPublish = useMemo(() => {
    const hasExisting = ["parent", "trading"].some((kind) => {
      const ids = getSupplierRecordIds(supplierSelectedRecords?.[kind]);
      return ids?.supplier_id != null || ids?.alias_id != null;
    });
    const hasDraftAdd = ["parent", "trading"].some((kind) => {
      return Boolean(String(supplierAdditions?.[kind]?.name || "").trim());
    });
    return hasExisting || hasDraftAdd;
  }, [supplierSelectedRecords, supplierAdditions]);

  const computedPublishReadiness = useMemo(() => {
    const confirmedLabels = Array.isArray(confirmedConfigPayload?.onboarding?.labels)
      ? confirmedConfigPayload.onboarding.labels
      : [];
    const labelsConfirmed = confirmedLabels.length > 0;
    const requiredInvoiceColumnsConfirmed = Boolean(invoiceMandatoryStatus?.allRequiredSatisfied);
    const supplierResolved = supplierResolvedForPublish;
    const canPublishLocal = labelsConfirmed && requiredInvoiceColumnsConfirmed && supplierResolved;
    const missing = [];
    if (!labelsConfirmed) missing.push("labels");
    if (!requiredInvoiceColumnsConfirmed) missing.push("required invoice columns");
    if (!supplierResolved) missing.push("supplier resolution");
    return {
      can_publish: canPublishLocal,
      labels_confirmed: labelsConfirmed,
      required_invoice_columns_confirmed: requiredInvoiceColumnsConfirmed,
      supplier_resolved: supplierResolved,
      missing,
    };
  }, [confirmedConfigPayload, invoiceMandatoryStatus, supplierResolvedForPublish]);

  // Use live computed readiness as primary — no need to save before Publish is enabled.
  // If an API response returned a stricter canPublish=false, respect it unless local state now passes.
  const effectiveCanPublish = computedPublishReadiness.can_publish || canPublish;
  const effectivePublishMissing = (() => {
    const base = computedPublishReadiness.can_publish
      ? []
      : computedPublishReadiness.missing.length > 0
        ? computedPublishReadiness.missing
        : publishMissing;
    return base;
  })();


  const normalizePublishReadiness = (responseData, fallbackReadiness = computedPublishReadiness) => {
    const payload = responseData && typeof responseData === "object" ? responseData : {};
    const fallback = fallbackReadiness && typeof fallbackReadiness === "object" ? fallbackReadiness : {};
    const checks = payload?.publish_checks && typeof payload.publish_checks === "object"
      ? payload.publish_checks
      : {};

    const labelsConfirmed = toBoolean(payload?.labels_confirmed ?? checks?.labels_confirmed ?? fallback?.labels_confirmed);
    const requiredInvoiceColumnsConfirmed = toBoolean(
      payload?.required_invoice_columns_confirmed
      ?? payload?.invoice_columns_confirmed
      ?? checks?.required_invoice_columns_confirmed
      ?? checks?.invoice_columns_confirmed
      ?? fallback?.required_invoice_columns_confirmed
      ?? fallback?.invoice_columns_confirmed
    );
    const supplierResolved = toBoolean(
      payload?.supplier_resolved
      ?? checks?.supplier_resolved
      ?? checks?.supplier_panel_saved
      ?? fallback?.supplier_resolved
      ?? fallback?.supplier_panel_saved
    );

    const explicitCanPublish = payload?.can_publish;
    const resolvedCanPublish = explicitCanPublish == null
      ? (labelsConfirmed && requiredInvoiceColumnsConfirmed && supplierResolved)
      : toBoolean(explicitCanPublish);

    const checkMissing = [];
    if (!labelsConfirmed) checkMissing.push("labels");
    if (!requiredInvoiceColumnsConfirmed) checkMissing.push("required invoice columns");
    if (!supplierResolved) checkMissing.push("supplier resolution");

    const payloadMissing = Array.isArray(payload?.missing) ? payload.missing : [];
    const missing = uniqueStrings([...payloadMissing, ...checkMissing]);

    return {
      canPublish: resolvedCanPublish,
      publishMissing: missing,
    };
  };

  const saveOnboardingDraft = async (nextStep = "supplier", options = {}) => {
    if (!dpaId) {
      triggerToastRef.current("warning", "Missing document ID — cannot save draft.", false, "Warning", false);
      setSaveFeedback({
        variant: "warning",
        message: "Save failed: missing document ID.",
      });
      return false;
    }

    const draftPayload = buildOnboardingDraftPayload(nextStep, options);
    const payloadToSave = { ...draftPayload };
    latestDraftPayloadRef.current = payloadToSave;

    if (!options?.includeSupplierSelection) {
      delete payloadToSave.supplier_update;
      if (payloadToSave?.confirmation_draft && typeof payloadToSave.confirmation_draft === "object") {
        delete payloadToSave.confirmation_draft.supplier_selection;
      }
    }

    const formData = new FormData();
    formData.append("_dpa_id", dpaId);

    // Labels-step saves send only { confirmed_labels, confirmation_draft }.
    // Supplier-step saves send { confirmed_labels, confirmation_draft, supplier_update }.
    formData.append("payload", JSON.stringify(payloadToSave || {}));

    try {
      const { responseData } = await fetchDataRef.current(
        "/update_document_onboarding.sz",
        "POST",
        formData,
        "form-data"
      );

      if (!responseData || responseData.st !== 1) {
        const readiness = normalizePublishReadiness(responseData, computedPublishReadiness);
        setCanPublish(readiness.canPublish);
        setPublishMissing(readiness.publishMissing);
        const msg = responseData?.mg || responseData?.msg || responseData?.m_en || "Unable to publish onboarding.";
        triggerToastRef.current("error", msg, false, "Save Error", false);
        setSaveFeedback({
          variant: "danger",
          message: `Save failed: ${msg}`,
        });
        return false;
      }

      const readiness = normalizePublishReadiness(responseData, computedPublishReadiness);
      const missingItems = readiness.publishMissing;
      setCanPublish(readiness.canPublish);
      setPublishMissing(missingItems);

      const saveMessage = readiness.canPublish
        ? "Saved successfully. All checks passed — ready to publish."
        : missingItems.length > 0
          ? `Saved. Remaining: ${missingItems.join(", ")}.`
          : "Saved successfully.";

      triggerToastRef.current("success", saveMessage, false, "Saved", false);
      setSaveFeedback({
        variant: readiness.canPublish ? "success" : "warning",
        message: saveMessage,
      });

      return true;
    } catch {
      triggerToastRef.current("error", "Unable to save onboarding draft.", true, "Save Error", false, 0);
      setSaveFeedback({
        variant: "danger",
        message: "Save failed: unable to reach server.",
      });
      return false;
    }
  };

  const allRequiredLabelsConfirmed = statementMandatoryStatus.allRequiredSatisfied && invoiceMandatoryStatus.allRequiredSatisfied;

  const getConfirmedStatementLabelValue = (labelType) => {
    const card = statementCards.find((item) => item?.label === labelType && validatedFields?.[item.id] && !deletedFields?.[item.id]);
    if (!card) return "";
    const ids = fieldOverrides?.[card.id]?.text_ids || card?.text_ids || (card?.text_id ? [card.text_id] : []);
    return getTextFromIds(ids) || card?.displayValue || "";
  };

  const effectiveSupplierResolution = useMemo(() => {
    if (hasSavedConfirmedData) {
      const confirmedResolution = savedConfirmedLabels?.meta?.supplierResolution;
      if (confirmedResolution && typeof confirmedResolution === "object") {
        return confirmedResolution;
      }
    }
    return supplierResolutionDraft && typeof supplierResolutionDraft === "object" ? supplierResolutionDraft : {};
  }, [hasSavedConfirmedData, savedConfirmedLabels, supplierResolutionDraft]);

  const getObservedSupplierName = (observedNames, sourceField) => {
    const rows = Array.isArray(observedNames) ? observedNames : [];
    const matchedRows = rows.filter((row) => row?.source_field === sourceField);
    const preferredRow = matchedRows.find((row) => Boolean(row?.exists) && String(row?.value || "").trim())
      || matchedRows.find((row) => String(row?.value || "").trim());
    return String(preferredRow?.value || "").trim();
  };

  const supplierConfirmData = useMemo(() => {
    const observed = Array.isArray(effectiveSupplierResolution?.observed_names) ? effectiveSupplierResolution.observed_names : [];
    const observedParentName = getObservedSupplierName(observed, "parent_supplier_name");
    const observedTradingName = getObservedSupplierName(observed, "trading_supplier_name");
    // A previously saved draft may carry the backend's "Not detected" placeholder —
    // clean each source independently so a poisoned saved value falls through to the
    // confirmed label instead of masking it.
    const clean = (v) => {
      const s = String(v || "").trim();
      return s && s !== "Not detected" ? s : "";
    };
    const parentName = clean(hasSavedConfirmedData ? observedParentName : "") || clean(getConfirmedStatementLabelValue("SupplierParentName"));
    const tradingName = clean(hasSavedConfirmedData ? observedTradingName : "") || clean(getConfirmedStatementLabelValue("SupplierTradingName"));
    const baseAddress = effectiveSupplierResolution?.address || {};

    const hasObservedMatch = (sourceField, value) => {
      const target = String(value || "").trim().toLowerCase();
      if (!target) return false;
      return observed.some((row) => {
        if (row?.source_field !== sourceField) return false;
        const rowValue = String(row?.value || "").trim().toLowerCase();
        return rowValue === target && Boolean(row?.exists);
      });
    };

    const parentExists = Boolean(supplierAdditions.parent) || hasObservedMatch("parent_supplier_name", parentName);
    const tradingExists = Boolean(supplierAdditions.trading) || hasObservedMatch("trading_supplier_name", tradingName);

    return {
      parentName,
      tradingName,
      parentExists,
      tradingExists,
      baseAddress,
    };
  }, [validatedFields, deletedFields, fieldOverrides, effectiveSupplierResolution, supplierAdditions, hasSavedConfirmedData]);

  const supplierExistsFor = (kind, value) => {
    const selected = String(value || "").trim().toLowerCase();
    if (!selected) return false;

    if (supplierSelectedRecords?.[kind]) return true;

    const addedName = String(supplierAdditions?.[kind]?.name || "").trim().toLowerCase();
    if (addedName && addedName === selected) return true;

    const sourceField = kind === "parent" ? "parent_supplier_name" : "trading_supplier_name";
    const observed = Array.isArray(effectiveSupplierResolution?.observed_names) ? effectiveSupplierResolution.observed_names : [];
    return observed.some((row) => {
      if (row?.source_field !== sourceField) return false;
      const rowValue = String(row?.value || "").trim().toLowerCase();
      return rowValue === selected && Boolean(row?.exists);
    });
  };

  const getSelectedSupplierName = (kind) => {
    // Use ?? so an empty string (user cleared the input) still falls through to the pre-filled name.
    // Do NOT trim here — trimming kills trailing spaces mid-typing (e.g. "PS 786").
    // supplierExistsFor() does its own trim internally.
    const selected = String(supplierSelections?.[kind] ?? "");
    if (selected !== "") return selected;
    return kind === "parent" ? supplierConfirmData.parentName : supplierConfirmData.tradingName;
  };

  const restoreSupplierSelectionRecord = (selection) => {
    const selectedIds = selection?.selected_ids && typeof selection.selected_ids === "object"
      ? selection.selected_ids
      : {};
    const selectedName = String(selection?.selected_name || "").trim();

    if (!selectedName && selectedIds?.supplier_id == null && selectedIds?.alias_id == null) {
      return null;
    }

    return {
      supplier_id: selectedIds?.supplier_id ?? null,
      alias_id: selectedIds?.alias_id ?? null,
      id: selectedIds?.alias_id ?? selectedIds?.supplier_id ?? null,
      name: selectedName,
      supplier_name: selectedName,
    };
  };

  const selectedParentName = getSelectedSupplierName("parent");
  const selectedTradingName = getSelectedSupplierName("trading");
  const hasSupplierSelection = Boolean(selectedParentName || selectedTradingName);
  const supplierNamesConfirmed =
    hasSupplierSelection &&
    (!selectedParentName || supplierExistsFor("parent", selectedParentName)) &&
    (!selectedTradingName || supplierExistsFor("trading", selectedTradingName));

  // Phase I: free-text supplier creation removed. Users must pick an existing
  // master from the search picker; openSupplierOverlay / handleAddSupplierFromOverlay
  // and the off-canvas overlay JSX were deleted.

  // ------------------------------------------------------------------
  // ALIAS GATE (spec §6): the scanned supplier text is looked up against
  // the alias table. Hit → the existing alias decides the payee, no re-ask.
  // Miss → the picker opens pre-seeded with the scanned (trading-first) name.
  // ------------------------------------------------------------------
  useEffect(() => {
    if (activeStep !== "supplier") return;
    const scanned = String(supplierConfirmData.tradingName || supplierConfirmData.parentName || "").trim();
    const slot = supplierConfirmData.tradingName ? "trading" : "parent";
    if (!scanned) {
      setPayeeGate({ status: "no_name", hit: null, scanned: "", slot: null });
      return;
    }
    let cancelled = false;
    setPayeeGate({ status: "checking", hit: null, scanned, slot });
    (async () => {
      try {
        const formData = new FormData();
        formData.append("_name", scanned);
        const { responseData } = await fetchDataRef.current("/lookupsupplieralias.sz", "POST", formData, "form-data");
        if (cancelled) return;
        const hit = responseData?.st === 1 && Array.isArray(responseData?.payload) && responseData.payload.length > 0
          ? responseData.payload[0]
          : null;
        setPayeeGate({ status: hit ? "alias_hit" : "needs_pick", hit, scanned, slot });
        if (hit) {
          // Existing alias wins: fill the slot so the publish plumbing
          // (supplier_update → document_onboarding columns) keeps working.
          setSupplierSelectedRecords((prev) => ({
            ...prev,
            [slot]: {
              supplier_id: hit.master_id,
              alias_id: hit.alias_id,
              id: hit.master_id,
              name: hit.master_name,
              supplier_name: hit.master_name,
            },
          }));
          setSupplierSelections((prev) => ({ ...prev, [slot]: hit.master_name }));
        }
      } catch {
        if (!cancelled) setPayeeGate({ status: "needs_pick", hit: null, scanned, slot });
      }
    })();
    return () => { cancelled = true; };
  }, [activeStep, supplierConfirmData.tradingName, supplierConfirmData.parentName]);

  const handlePayeePicked = async (record, allResults) => {
    const slot = payeeGate.slot || "trading";
    setSupplierSelectedRecords((prev) => ({ ...prev, [slot]: record }));
    setSupplierSelections((prev) => ({ ...prev, [slot]: record?.name || "" }));
    setShowPayeePicker(false);
    setPayeePickerResults(allResults || []);
    setMergeRequests([]);
    // The saved status line ("Saved. Remaining: …supplier resolution") is computed at
    // save time — picking a payee satisfies it locally, so don't keep showing it stale.
    setSaveFeedback(null);
    // Merge-prompt candidates must rank against the SCANNED statement name,
    // not whatever the user last typed in the picker.
    const scanned = String(payeeGate.scanned || "").trim();
    if (scanned.length < 2) {
      setMergeCandidates([]);
      return;
    }
    let scannedResults = [];
    try {
      const formData = new FormData();
      formData.append("_q", scanned);
      const { responseData } = await fetchDataRef.current("/searchsuppliers.sz", "POST", formData, "form-data");
      scannedResults = responseData?.st === 1 && Array.isArray(responseData?.payload) ? responseData.payload : [];
    } catch {
      scannedResults = [];
    }
    const strong = scannedResults.filter(
      (r) => Number(r?.id) !== Number(record?.id) && (Number(r?.rank) >= 700 || Number(r?.sim) >= 0.65)
    );
    const seen = new Set();
    const candidates = strong.filter((r) => {
      if (seen.has(r.id)) return false;
      seen.add(r.id);
      return true;
    }).slice(0, 4);
    setMergeCandidates(candidates.map((r) => ({ id: r.id, name: r.name })));
  };

  const handleConfirmRelink = async () => {
    const hit = payeeGate.hit;
    const target = pendingRelink;
    if (!hit?.alias_id || !target?.id) return;
    try {
      const formData = new FormData();
      formData.append("_alias_id", hit.alias_id);
      formData.append("_master_id", target.id);
      formData.append("_surface", "onboarding");
      const { responseData } = await fetchDataRef.current("/relinksupplieralias.sz", "POST", formData, "form-data");
      const result = Array.isArray(responseData?.payload) ? responseData.payload[0] : null;
      if (responseData?.st === 1 && result?.status !== "error") {
        triggerToastRef.current("success",
          `Future statements named “${hit.alias_name_raw || payeeGate.scanned}” will resolve to ${target.name}.`,
          false, "Payee changed", false);
        setPayeeGate((prev) => ({
          ...prev,
          hit: { ...prev.hit, master_id: target.id, master_name: target.name },
        }));
        const slot = payeeGate.slot || "trading";
        setSupplierSelectedRecords((prev) => ({
          ...prev,
          [slot]: { supplier_id: target.id, alias_id: hit.alias_id, id: target.id, name: target.name, supplier_name: target.name },
        }));
        setSupplierSelections((prev) => ({ ...prev, [slot]: target.name }));
      } else {
        triggerToastRef.current("warning", result?.reason || responseData?.mg || "Could not change the payee.", false, "", false);
      }
    } catch {
      triggerToastRef.current("error", "We seem to have an issue accessing your data, please try again later.", true, "Network Issue", false, 0);
    } finally {
      setPendingRelink(null);
    }
  };

  const handleNextStep = async () => {
    if (!allRequiredLabelsConfirmed) return;

    setIsSavingDraft(true);
    try {
      const saved = await saveOnboardingDraft("supplier");
      if (!saved) return;

      setActiveField("");
      setHoveredField("");
      setDrawFieldId("");
      setDrawStart(null);
      setDrawPreview(null);
      setActiveStep("supplier");
    } finally {
      setIsSavingDraft(false);
    }
  };

  const handleSaveSupplierStep = async () => {
    setIsSavingDraft(true);
    try {
      await saveOnboardingDraft("supplier", { includeSupplierSelection: true });
    } finally {
      setIsSavingDraft(false);
    }
  };

  const handlePublish = async () => {
    if (isPublishing || isSavingDraft) return;

    if (!dpaId) {
      triggerToastRef.current("warning", "Missing document ID — cannot publish onboarding.", false, "Warning", false);
      return;
    }

    if (!effectiveCanPublish) {
      const missingMessage = effectivePublishMissing.length > 0
        ? `Publish blocked. Missing: ${effectivePublishMissing.join(", ")}.`
        : "Publish blocked. Please complete required fields first.";
      triggerToastRef.current("warning", missingMessage, false, "Not ready", false);
      setSaveFeedback({ variant: "warning", message: missingMessage });
      return;
    }

    // Flush the supplier selection (payee + merge requests) before publishing —
    // the proc reads the persisted row, and a labels-step save may have
    // replaced confirmation_draft without supplier_selection.
    setIsSavingDraft(true);
    try {
      const saved = await saveOnboardingDraft("supplier", { includeSupplierSelection: true });
      if (!saved) {
        triggerToastRef.current("warning", "Could not save the supplier selection — publish cancelled.", false, "Not saved", false);
        return;
      }
    } finally {
      setIsSavingDraft(false);
    }

    setIsPublishing(true);
    try {
      // Publish does not send a payload — the backend loads and publishes from the persisted document_onboarding row.
      const formData = new FormData();
      formData.append("_dpa_id", dpaId);

      const { responseData } = await fetchDataRef.current(
        PUBLISH_DOCUMENT_ONBOARDING_ENDPOINT,
        "POST",
        formData,
        "form-data"
      );

      const readiness = normalizePublishReadiness(responseData, computedPublishReadiness);
      setCanPublish(readiness.canPublish);
      setPublishMissing(readiness.publishMissing);

      if (!responseData || responseData?.st !== 1) {
        const msg = responseData?.msg || responseData?.m_en || responseData?.mg || "Unable to publish onboarding.";
        triggerToastRef.current("error", msg, false, "Publish Error", false);
        setSaveFeedback({ variant: "danger", message: `Publish failed: ${msg}` });
        return;
      }

      const published = toBoolean(responseData?.published ?? true);
      const statusText = String(responseData?.status || (published ? "published" : "saved"));
      const message = responseData?.mg || responseData?.msg || responseData?.m_en || "Successfully published.";

      setPublishResult({
        status: statusText,
        msg: message,
        config_id: responseData?.config_id ?? null,
        config_profile_id: responseData?.config_profile_id ?? null,
        published,
      });

      // If backend returns refreshed onboarding payload, keep local state in sync.
      if (responseData?.payload != null) {
        setOnboardData(responseData.payload);
      }

      setSaveFeedback({ variant: "success", message });
      triggerToastRef.current("success", message, false, "Published", false);
    } catch {
      triggerToastRef.current("error", "Unable to publish onboarding.", true, "Publish Error", false, 0);
      setSaveFeedback({ variant: "danger", message: "Publish failed: unable to reach server." });
    } finally {
      setIsPublishing(false);
    }
  };

  // PDF centering calculation - from PdfModal
  useEffect(() => {
    if (!show) return;
    hasHydratedOnboardRef.current = false;
    setPageNo(1);
    setCurrentPage(1);
    setNumPages(null);
    setScale(1);
    setActiveField("");
    setHoveredField("");
    setValidatedFields({});
    setDeletedFields({});
    setFieldOverrides({});
    setDrawFieldId("");
    setDrawStart(null);
    setDrawPreview(null);
    setActiveStep("labels");
    setIsSavingDraft(false);
    setIsPublishing(false);
    setSaveFeedback(null);
    setPublishResult(null);
    setCanPublish(false);
    setPublishMissing([]);
    setShowSupplierAddOverlay(false);
    setSupplierAddKind("parent");
    setSupplierAdditions({ parent: null, trading: null });
    setSupplierSelections({ parent: "", trading: "" });
    setSupplierSelectedRecords({ parent: null, trading: null });
    setPayeeGate({ status: "idle", hit: null, scanned: "", slot: null });
    setShowPayeePicker(false);
    setPayeePickerResults([]);
    setMergeCandidates([]);
    setMergeRequests([]);
    setPendingRelink(null);
    setSupplierAddDraft({
      name: "",
      street: "",
      city: "",
      province: "",
      postal_code: "",
      country: "",
    });
    setSeparatorOverrides({});
    setDraggingSep(null);
  }, [show]);

  useEffect(() => {
    if (!show) return;
    hasHydratedOnboardRef.current = false;
  }, [show, onboardData]);

  useEffect(() => {
    const previousCursor = document.body.style.cursor;
    if (drawFieldId) {
      document.body.style.cursor = "crosshair";
    }
    return () => {
      document.body.style.cursor = previousCursor;
    };
  }, [drawFieldId]);

  // [ZONE MATCHING] Initiates a separator drag. Reads the container width from the DOM at call
  // time so we don't need pdfPixelWidth (defined later in the component) in the closure.
  // pairedKey: when set, both key and pairedKey are updated simultaneously (paired separator).
  // minX/maxX: drag constraints so handles can't cross their neighbors.
  // Stable reference (empty deps) — canvasRef and state setters are always stable.
  const handleSeparatorDragStart = useCallback((e, sepKey, currentX, pairedKey, minX, maxX) => {
    const containerWidth = canvasRef.current?.getBoundingClientRect().width || 0;
    setDraggingSep({
      key: sepKey, pairedKey: pairedKey ?? null,
      startClientX: e.clientX, startSepX: currentX, containerWidth,
      minX: minX ?? 0.01, maxX: maxX ?? 0.99,
    });
  }, []);

  // [ZONE MATCHING] Global mousemove/mouseup handlers for separator dragging.
  // Only active while draggingSep is set; cleaned up on mouseup or unmount.
  // pairedKey: paired separator sets both keys to the same x (no gap between columns).
  // minX/maxX: clamp range so handles can't cross adjacent handles.
  useEffect(() => {
    if (!draggingSep) return;
    const handleMouseMove = (e) => {
      const { startClientX, startSepX, containerWidth: cw, key, pairedKey, minX, maxX } = draggingSep;
      if (!cw) return;
      const newX = Math.max(minX, Math.min(maxX, startSepX + (e.clientX - startClientX) / cw));
      setSeparatorOverrides((prev) => {
        const next = { ...prev, [key]: newX };
        if (pairedKey) next[pairedKey] = newX;
        return next;
      });
    };
    const handleMouseUp = () => setDraggingSep(null);
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup",   handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup",   handleMouseUp);
    };
  }, [draggingSep]);

  // [ZONE MATCHING] Clicking a zone stripe label toggles that column as the active field,
  // which dims the other zone stripes and highlights the heading bounding box — same result
  // as clicking the column card in the offcanvas. Click the same stripe again to deselect.
  const handleZoneClick = useCallback((fieldId) => {
    if (drawFieldId) return;
    setActiveField((prev) => (prev === fieldId ? "" : fieldId));
  }, [drawFieldId]);

  const getPointInCanvas = (event) => {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect || rect.width <= 0 || rect.height <= 0) return null;
    // Normalize against pdfPixelWidth/pdfPixelHeight (PDF page dimensions × scale) so that
    // nx/ny are in the same 0-1 Document AI coordinate space as token bboxes. rect.width
    // can diverge from pdfPixelWidth when the container is scrollbar-constrained or
    // flex-aligned, causing a rightward shift that selects the wrong column.
    const docWidth = pdfPixelWidth > 0 ? pdfPixelWidth : rect.width;
    const docHeight = pdfPixelHeight > 0 ? pdfPixelHeight : rect.height;
    const px = Math.max(0, Math.min(event.clientX - rect.left, rect.width));
    const py = Math.max(0, Math.min(event.clientY - rect.top, rect.height));
    return {
      px,
      py,
      nx: px / docWidth,
      ny: py / docHeight,
      width: rect.width,
      height: rect.height,
    };
  };

  const buildPreviewRect = (a, b) => {
    const left = Math.min(a.px, b.px);
    const top = Math.min(a.py, b.py);
    const width = Math.max(Math.abs(b.px - a.px), 1);
    const height = Math.max(Math.abs(b.py - a.py), 1);
    return { left, top, width, height };
  };

  const handleDrawStart = (e) => {
    if (!drawFieldId) return;
    const point = getPointInCanvas(e);
    if (!point) return;
    setDrawStart(point);
    setDrawPreview({ left: point.px, top: point.py, width: 1, height: 1 });
  };

  const handleDrawMove = (e) => {
    if (!drawFieldId || !drawStart) return;
    const point = getPointInCanvas(e);
    if (!point) return;
    setDrawPreview(buildPreviewRect(drawStart, point));
  };

  const handleDrawEnd = (e) => {
    if (!drawFieldId || !drawStart) return;
    const end = getPointInCanvas(e);
    if (!end) {
      setDrawStart(null);
      setDrawPreview(null);
      setDrawFieldId("");
      return;
    }

    const minX = Math.min(drawStart.nx, end.nx);
    const minY = Math.min(drawStart.ny, end.ny);
    const maxX = Math.max(drawStart.nx, end.nx);
    const maxY = Math.max(drawStart.ny, end.ny);

    // FIX: Column-snapping for column-type fields.
    // The token-intersection approach below is unreliable for column heading tokens — heading text
    // sits in a very narrow y-band (~0.01 normalized height) and any coordinate drift between
    // getBoundingClientRect and Document AI normalized coordinates causes zero tokens to intersect.
    // Result: silent failure (selectedKeys.length === 0) → fieldOverrides never set → falls back
    // to savedGeometry (Due Date from a prior save) → displays Due Date every time.
    //
    const drawField = fieldRegistry[drawFieldId];
    if (drawField?.selectionType === "column") {
      const xCenter = (minX + maxX) / 2;
      const headingPage = Number(resolvedTableHeadings?.page || pageNo);

      const commitColumnTokens = (tokenIds) => {
        setFieldOverrides((prev) => ({ ...prev, [drawFieldId]: { text_ids: tokenIds } }));
        setDeletedFields((prev) => ({ ...prev, [drawFieldId]: false }));
        setValidatedFields((prev) => ({ ...prev, [drawFieldId]: false }));
        setActiveField(drawFieldId);
        setDrawStart(null);
        setDrawPreview(null);
        setDrawFieldId("");
      };

      // Primary: search for header tokens directly at the drawn x-range.
      // This works even when the pre-detected column list is incomplete or wrong.
      // getColumnHeaderTokens handles thin header y-bands via sharedInvoiceHeaderAnchor.
      const directTokens = getColumnHeaderTokens({ x_min: minX, x_max: maxX }, sourceTokens, headingPage, 0);
      const directTokenIds = directTokens.map((t) => t?.key).filter(Boolean);
      if (directTokenIds.length > 0) {
        commitColumnTokens(directTokenIds);
        return;
      }

      // Secondary: fall back to nearest pre-detected column by x_mid proximity.
      const allColumns = Array.isArray(resolvedTableHeadings?.columns) ? resolvedTableHeadings.columns : [];
      const ranked = allColumns
        .map((col) => {
          const colXMin = Number(col?.x_min);
          const colXMax = Number(col?.x_max);
          const colXMid = Number.isFinite(Number(col?.x_mid))
            ? Number(col.x_mid)
            : Number.isFinite(colXMin) && Number.isFinite(colXMax)
              ? (colXMin + colXMax) / 2
              : null;
          if (!Number.isFinite(colXMid)) return null;
          return { col, colXMid, distance: Math.abs(xCenter - colXMid) };
        })
        .filter(Boolean)
        .sort((a, b) => a.distance - b.distance);

      const best = ranked[0];
      if (best) {
        const snappedIds = getColumnTokenIds(best.col, drawField.labelType, headingPage);
        if (snappedIds.length > 0) {
          commitColumnTokens(snappedIds);
          return;
        }
      }
      // Tertiary: fall through to token intersection.
    }

    const intersects = (bbox) => {
      const bx1 = Number(bbox?.x);
      const by1 = Number(bbox?.y);
      const bx2 = Number(bbox?.x2);
      const by2 = Number(bbox?.y2);
      if (![bx1, by1, bx2, by2].every(Number.isFinite)) return false;
      return !(bx2 < minX || bx1 > maxX || by2 < minY || by1 > maxY);
    };

    const selectedKeys = sourceTokens
      .filter((token) => Number(token?.page || 1) === Number(pageNo))
      .filter((token) => intersects(token?.bbox))
      .sort((a, b) => Number(a?.bbox?.y || 0) - Number(b?.bbox?.y || 0) || Number(a?.bbox?.x || 0) - Number(b?.bbox?.x || 0))
      .map((token) => token?.key)
      .filter(Boolean);

    if (selectedKeys.length > 0) {
      setFieldOverrides((prev) => ({
        ...prev,
        [drawFieldId]: { text_ids: selectedKeys },
      }));
      setDeletedFields((prev) => ({
        ...prev,
        [drawFieldId]: false,
      }));
      setValidatedFields((prev) => ({
        ...prev,
        [drawFieldId]: false,
      }));
      setActiveField(drawFieldId);
    }

    setDrawStart(null);
    setDrawPreview(null);
    setDrawFieldId("");
  };

  const handleMouseDown = () => {
    setIsDragging(true);
    document.body.classList.add("bc-dragging");
  };

  const handleMouseMove = (e) => {
    if (dragRef.current) cancelAnimationFrame(dragRef.current);
    dragRef.current = requestAnimationFrame(() => {
      const maxWidth = Math.max(320, window.innerWidth - 280);
      const newWidth = Math.min(Math.max(260, window.innerWidth - e.clientX), maxWidth);
      setRightPanelWidth(newWidth);
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    document.body.classList.remove("bc-dragging");
    if (dragRef.current) {
      cancelAnimationFrame(dragRef.current);
      dragRef.current = null;
    }
  };

  useEffect(() => {
    if (isDragging) {
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
    } else {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    }
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging]);

  useEffect(() => {
    if (!show) return;

    const loadOnboard = async () => {
      setIsLoadingOnboard(true);
      const url = "/getonboard.sz";
      const method = "POST";
      const formData = new FormData();

      if (dpaId) formData.append("_dpa_id", dpaId);

      try {
        const { responseData } = await fetchDataRef.current(url, method, formData, "application/json");
        if (responseData?.st === 1) {
          setOnboardData(responseData?.payload ?? null);
          const readiness = normalizePublishReadiness(responseData, computedPublishReadiness);
          setCanPublish(readiness.canPublish);
          setPublishMissing(readiness.publishMissing);

          const alreadyPublished = toBoolean(responseData?.published || responseData?.payload?.published);
          if (alreadyPublished) {
            setPublishResult({
              status: String(responseData?.status || responseData?.payload?.status || "published"),
              msg: responseData?.msg || responseData?.m_en || responseData?.mg || "Successfully published.",
              config_id: responseData?.config_id ?? responseData?.payload?.config_id ?? null,
              config_profile_id: responseData?.config_profile_id ?? responseData?.payload?.config_profile_id ?? null,
              published: true,
            });
          }
        } else {
          setOnboardData(null);
          triggerToastRef.current("warning", responseData?.m_en || "Unable to load onboarding data.", false, "", false);
        }
      } catch (error) {
        setOnboardData(null);
        triggerToastRef.current("error", "Unable to load onboarding data.", true, "Issue", false, 0);
      } finally {
        setIsLoadingOnboard(false);
      }
    };

    loadOnboard();
  }, [show, dpaId, invStatId]);

  useEffect(() => {
    if (!show || isLoadingOnboard || hasHydratedOnboardRef.current) return;

    const draft = savedConfirmationDraft;
    const confirmedLabelRows = Array.isArray(savedConfirmedLabels?.labels) ? savedConfirmedLabels.labels : [];
    const confirmedColumnRows = Array.isArray(savedConfirmedLabels?.tableHeadings)
      ? savedConfirmedLabels.tableHeadings.flatMap((heading) => Array.isArray(heading?.columns) ? heading.columns : [])
      : [];
    const ignoredLabelRows = Array.isArray(savedConfirmedLabels?.ignored_labels) ? savedConfirmedLabels.ignored_labels : [];
    const ignoredColumnRows = Array.isArray(savedConfirmedLabels?.ignored_columns) ? savedConfirmedLabels.ignored_columns : [];

    const fallbackValidated = [...confirmedLabelRows, ...confirmedColumnRows].reduce((acc, row) => {
      if (row?.field_id) acc[row.field_id] = true;
      return acc;
    }, {});

    const fallbackDeleted = [...ignoredLabelRows, ...ignoredColumnRows].reduce((acc, row) => {
      if (row?.field_id) acc[row.field_id] = true;
      return acc;
    }, {});

    const fallbackFieldOverrides = [...confirmedLabelRows, ...confirmedColumnRows, ...ignoredLabelRows, ...ignoredColumnRows].reduce((acc, row) => {
      if (!row?.field_id) return acc;
      if (Array.isArray(row?.text_ids) && row.text_ids.length) {
        acc[row.field_id] = { text_ids: row.text_ids };
      }
      return acc;
    }, {});

    const confirmedFieldIds = new Set(
      [...confirmedLabelRows, ...confirmedColumnRows]
        .map((row) => String(row?.field_id || "").trim())
        .filter(Boolean)
    );

    const hasKeys = (value) => value && typeof value === "object" && !Array.isArray(value) && Object.keys(value).length > 0;

    const restoredValidated = hasSavedConfirmedData
      ? (hasKeys(draft?.validated_fields) ? draft.validated_fields : fallbackValidated)
      : {};
    const restoredDeleted = hasSavedConfirmedData
      ? (hasKeys(draft?.deleted_fields) ? { ...fallbackDeleted, ...draft.deleted_fields } : fallbackDeleted)
      : {};
    const restoredOverrides = hasSavedConfirmedData
      ? (() => {
          const merged = { ...fallbackFieldOverrides };
          if (!hasKeys(draft?.field_overrides)) return merged;

          Object.entries(draft.field_overrides).forEach(([fieldId, override]) => {
            const normalizedFieldId = String(fieldId || "").trim();
            if (!normalizedFieldId) return;

            // Confirmed labels/columns are authoritative.
            // This avoids stale draft overrides swapping cards on first load.
            if (confirmedFieldIds.has(normalizedFieldId)) return;

            merged[normalizedFieldId] = override;
          });

          return merged;
        })()
      : {};

    setValidatedFields(restoredValidated);
    setDeletedFields(restoredDeleted);
    setFieldOverrides(restoredOverrides);

    const normalizedSupplierSelection = normalizeSavedSupplierSelection(savedSupplierSelection);
    const parentSelection = normalizedSupplierSelection.parent;
    const tradingSelection = normalizedSupplierSelection.trading;

    setSupplierSelections({
      parent: String(parentSelection?.search_text ?? parentSelection?.selected_name ?? ""),
      trading: String(tradingSelection?.search_text ?? tradingSelection?.selected_name ?? ""),
    });

    setSupplierSelectedRecords({
      parent: parentSelection?.mode === "existing" ? restoreSupplierSelectionRecord(parentSelection) : null,
      trading: tradingSelection?.mode === "existing" ? restoreSupplierSelectionRecord(tradingSelection) : null,
    });

    setSupplierAdditions({
      parent: parentSelection?.mode === "draft_add" && parentSelection?.draft_add
        ? { ...parentSelection.draft_add }
        : null,
      trading: tradingSelection?.mode === "draft_add" && tradingSelection?.draft_add
        ? { ...tradingSelection.draft_add }
        : null,
    });

    const hasSupplierResolvedFromSavedSelection = [parentSelection, tradingSelection].some((selection) => {
      if (!selection || typeof selection !== "object") return false;
      if (selection?.mode === "existing") {
        return selection?.selected_ids?.supplier_id != null || selection?.selected_ids?.alias_id != null;
      }
      if (selection?.mode === "draft_add") {
        return Boolean(String(selection?.draft_add?.name || selection?.selected_name || "").trim());
      }
      return false;
    });

    const confirmedRequiredInvoiceColumns = MANDATORY_INVOICE_COLUMNS.every((requiredType) => {
      return confirmedColumnRows.some((column) => column?.labelType === requiredType);
    });

    const readinessFallbackFromSaved = {
      labels_confirmed: confirmedLabelRows.length > 0,
      required_invoice_columns_confirmed: confirmedRequiredInvoiceColumns,
      supplier_resolved: hasSupplierResolvedFromSavedSelection,
      missing: [],
    };
    const readinessFromLoaded = normalizePublishReadiness(rootPayload, readinessFallbackFromSaved);
    setCanPublish(readinessFromLoaded.canPublish);
    setPublishMissing(readinessFromLoaded.publishMissing);

    const alreadyPublished = toBoolean(rootPayload?.published || rootPayload?.is_published);
    if (alreadyPublished) {
      setPublishResult({
        status: String(rootPayload?.status || "published"),
        msg: String(rootPayload?.msg || rootPayload?.m_en || "Successfully published."),
        config_id: rootPayload?.config_id ?? null,
        config_profile_id: rootPayload?.config_profile_id ?? null,
        published: true,
      });
    }

    setActiveStep("labels");
    hasHydratedOnboardRef.current = true;
  }, [show, isLoadingOnboard, hasSavedConfirmedData, savedConfirmedLabels, savedConfirmationDraft, savedSupplierSelection, rootPayload]);

  const zoomIn = () => setScale(scale + 0.2);
  const zoomOut = () => setScale(scale - 0.2);
  const actualSize = () => setScale(1.0);
  
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    setPageNo(pageNumber);
  };

  const handleDocumentLoadSuccess = (pdf) => {
    setNumPages(pdf.numPages);
    setCurrentPage(1);
    setPageNo(1);
    pdf.getPage(1).then((page) => {
      const { view } = page;
      setPageDimensions({
        width: view[2],
        height: view[3],
      });
    });
  };

  const handleScroll = (event) => {
    scrollRef.current = event.target.scrollTop;
  };
  
  const visibleStatementCards = useMemo(() => {
    return statementCards;
  }, [statementCards]);

  const visibleInvoiceMappings = useMemo(() => {
    return invoiceMappings;
  }, [invoiceMappings]);

  const renderFieldCard = (item, color, options = {}) => {
    const isMandatory = Boolean(options?.isMandatory);
    const requirementSatisfied = Boolean(options?.requirementSatisfied);
    const isActive = activeField === item?.id;
    const isHovered = hoveredField === item?.id;
    const isValidated = Boolean(validatedFields?.[item?.id]);
    const isDeleted = Boolean(deletedFields?.[item?.id]);
    const hasRectangle = !isDeleted && handleSelect(item).length > 0;
    const isCheckDisabled = !hasRectangle;
    const isTrashDisabled = isDeleted;
    const isInteractive = isActive || isHovered;
    const overrideIds = fieldOverrides?.[item?.id]?.text_ids;
    const overrideText = Array.isArray(overrideIds) ? getTextFromIds(overrideIds) : "";
    const panelDisplayValue = isDeleted
      ? (item?.selectionType === "column" ? "Not mapped" : "—")
      : (overrideText || item?.displayValue || "—");
    const iconDim = "#adb5bd";
    const checkColor = isCheckDisabled ? iconDim : isValidated || isInteractive ? "#198754" : iconDim;
    const trashColor = isTrashDisabled ? iconDim : isInteractive ? "#dc3545" : iconDim;
    const rectColor = isInteractive ? color : iconDim;
    const borderColor = isValidated ? "#198754" : isActive || isHovered ? "#0d6efd" : "#dee2e6";
    const mandatoryAccent = isMandatory ? (requirementSatisfied ? "#198754" : "#ffc107") : "transparent";
    const requiredMarkerColor = requirementSatisfied ? "#198754" : "#b02a37";
    // [ZONE MATCHING] Left-border accent: invoice column cards use their zone stripe color so the
    // card is visually coupled to its stripe on the PDF. Statement field cards keep the mandatory
    // amber/green accent. This makes the coupling immediately obvious without any extra UI.
    const zoneAccentColor = item?.selectionType === "column"
      ? (ZONE_COLORS[item.labelType] || null)
      : null;
    const leftAccentColor = zoneAccentColor || mandatoryAccent;
    const hasLeftAccent   = Boolean(zoneAccentColor) || isMandatory;

    const handleValidateClick = (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (isCheckDisabled) return;
      setValidatedFields((prev) => ({
        ...prev,
        [item.id]: true,
      }));
    };

    const handleDeleteClick = (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (isTrashDisabled) return;
      setDeletedFields((prev) => ({
        ...prev,
        [item.id]: true,
      }));
      setValidatedFields((prev) => ({
        ...prev,
        [item.id]: false,
      }));
      setFieldOverrides((prev) => ({
        ...prev,
        [item.id]: { text_ids: [] },
      }));
      setActiveField(item.id);
    };

    const handleRectangleClick = (e) => {
      e.preventDefault();
      e.stopPropagation();
      setDrawFieldId(item.id);
      setActiveField(item.id);
    };

    return (
      <div
        key={item?.id}
        role="button"
        tabIndex={0}
        className="rounded p-2 text-start bg-white"
        style={{
          flex: "0 0 210px",
          width: 210,
          minWidth: 210,
          minHeight: 110,
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          border: `1px solid ${borderColor}`,
          boxShadow: hasLeftAccent ? `inset 3px 0 0 ${leftAccentColor}` : "none",
          cursor: "pointer",
        }}
        onClick={() => handleFieldClick(item, item.id)}
        onMouseEnter={() => setHoveredField(item?.id || "")}
        onMouseLeave={() => setHoveredField((prev) => (prev === item?.id ? "" : prev))}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleFieldClick(item, item.id);
          }
        }}
      >
        <div>
          <div className="fw-semibold small text-wrap">
            {item?.displayName || "Label"}
            {isMandatory && (
              <span
                className="ms-1"
                style={{ color: requiredMarkerColor, fontSize: "0.75rem", verticalAlign: "text-top" }}
                title={requirementSatisfied ? "Required (confirmed)" : "Required"}
                aria-label={requirementSatisfied ? "Required confirmed" : "Required"}
              >
                ●
              </span>
            )}
          </div>
          <div className="small text-wrap">{panelDisplayValue}</div>
        </div>
        <div className="d-flex align-items-center gap-3 mt-2">
          <button
            type="button"
            onClick={handleValidateClick}
            disabled={isCheckDisabled}
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            className="p-0 border-0 bg-transparent d-inline-flex align-items-center"
            title="Validate label"
            aria-label="Validate label"
          >
            <FaCheck style={{ color: checkColor, cursor: isCheckDisabled ? "not-allowed" : "pointer" }} />
          </button>
          <button
            type="button"
            onClick={handleDeleteClick}
            disabled={isTrashDisabled}
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            className="p-0 border-0 bg-transparent d-inline-flex align-items-center"
            title="Remove label"
            aria-label="Remove label"
          >
            <FaTrash style={{ color: trashColor, cursor: isTrashDisabled ? "not-allowed" : "pointer" }} />
          </button>
          <button
            type="button"
            onClick={handleRectangleClick}
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            className="p-0 border-0 bg-transparent d-inline-flex align-items-center"
            title="Highlight region"
            aria-label="Highlight region"
          >
            <FaVectorSquare style={{ color: rectColor }} />
          </button>
        </div>
      </div>
    );
  };

  const pdfPixelWidth = pageDimensions.width ? pageDimensions.width * scale : 0;
  const pdfPixelHeight = pageDimensions.height ? pageDimensions.height * scale : 0;

  return (
    <Modal
      show={show}
      onHide={onHide}
      fullscreen
      animation
      enforceFocus={false}
      dialogClassName="invoice-configure-modal"
      contentClassName="p-0 border-0 bg-white"
      style={{ zIndex: 2000 }}
    >
      {/* Top Panel */}
      <div className="invoice-configure-top-panel border-bottom px-3 px-md-4 py-2">
        <div className="d-flex align-items-center gap-3 h-100">
          <div className="invoice-configure-filename fw-semibold text-truncate">{fileName}</div>

          <button className="btn btn-link d-inline-flex align-items-center pt-1 pb-1 text-primary rounded-pill btn-link-offcanvas-border ms-auto" onClick={onHide}>
            <FaTimes size={15} className="me-2" />
            Close
          </button>
        </div>
      </div>

      {/* Body: PDF + Right Panel */}
      <div className="d-flex w-100" style={{ minHeight: "calc(100vh - 62px)", position: "relative" }}>
        {/* PDF Panel */}
        <div className="flex-grow-1 d-flex flex-column" style={{ minWidth: 0, overflow: "hidden" }}>
          {statementImageData ? (
            <>
              {/* PDF Controls */}
              <div className="d-flex justify-content-between align-items-center px-4 py-2 border-bottom bg-white">
                <div>
                  <FaSearchPlus size={22} onClick={zoomIn} title="Zoom In" style={{ cursor: "pointer" }} className="me-3" />
                  <FaSearchMinus size={22} onClick={zoomOut} title="Zoom Out" style={{ cursor: "pointer" }} className="me-3" />
                  <FaCompress size={22} onClick={actualSize} title="Actual Size" style={{ cursor: "pointer" }} />
                </div>
                <div className="d-flex flex-column justify-content-center align-items-center text-center">
                  {(docObj?.uploadOn || docObj?.uploadBy) && (
                    <div className="text-muted">
                      <small>Uploaded On</small> {docObj?.uploadOn ? format(new Date(docObj.uploadOn), "MMM dd, yyyy") : "N/A"} <small>by</small> {docObj?.uploadBy || "Unknown"}
                    </div>
                  )}
                  {docObj?.name && (
                    <div className="text-muted">
                      <small>File</small> {docObj.name}
                    </div>
                  )}
                </div>
                <div className="d-flex align-items-center">
                  <button
                    className="btn btn-link fs-5 pe-1"
                    onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                  >
                    <FaArrowUp size={20} />
                  </button>
                  <input
                    type="number"
                    className="form-control text-center"
                    value={currentPage}
                    onChange={(e) => {
                      let newPage = e.target.value;
                      if (newPage === "" || (!isNaN(newPage) && newPage >= 1 && newPage <= numPages)) {
                        setCurrentPage(newPage);
                      }
                    }}
                    onBlur={(e) => {
                      let newPage = parseInt(e.target.value, 10);
                      if (isNaN(newPage) || newPage < 1) {
                        setCurrentPage(1);
                      } else if (newPage > numPages) {
                        setCurrentPage(numPages);
                      } else {
                        setCurrentPage(newPage);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        let newPage = parseInt(e.target.value, 10);
                        if (!isNaN(newPage) && newPage >= 1 && newPage <= numPages) {
                          handlePageChange(newPage);
                          e.target.blur();
                        }
                      }
                    }}
                    style={{ width: "50px", margin: "0 5px" }}
                  />
                  <span className="text-muted"> of {numPages}</span>
                  <button
                    className="btn btn-link fs-5 pe-1"
                    onClick={() => handlePageChange(Math.min(numPages, currentPage + 1))}
                    disabled={currentPage === numPages}
                  >
                    <FaArrowDown size={20} />
                  </button>
                </div>
              </div>

              {/* PDF Viewer */}
              <div
                onScroll={handleScroll}
                className="d-flex flex-column align-items-center pt-4"
                style={{
                  overflowY: "auto",
                  overflowX: "hidden",
                  maxHeight: `calc(100vh - 140px)`,
                  width: "100%",
                  clipPath: "inset(0px 0px 0px 0px)",
                }}
              >
                <div
                  ref={canvasRef}
                  style={{
                    position: "relative",
                    display: "inline-block",
                    width: pdfPixelWidth ? `${pdfPixelWidth}px` : "auto",
                    height: pdfPixelHeight ? `${pdfPixelHeight}px` : "auto",
                    cursor: drawFieldId ? "crosshair" : "default",
                  }}
                  onMouseDown={handleDrawStart}
                  onMouseMove={handleDrawMove}
                  onMouseUp={handleDrawEnd}
                  onMouseLeave={handleDrawEnd}
                >
                  <Document file={`data:application/pdf;base64,${statementImageData}`} onLoadSuccess={handleDocumentLoadSuccess}>
                    <Page
                      pageNumber={pageNo}
                      scale={scale}
                      renderTextLayer={false}
                      renderAnnotationLayer={false}
                      style={{ width: "100%" }}
                    />
                  </Document>
                  {/* [ZONE MATCHING] Column zone stripes + draggable separator handles.
                      Rendered before PassiveOverlayBoxes so zone stripes (no zIndex) sit behind
                      bounding boxes (zIndex 5). Separator handles use zIndex 8 to sit above all
                      overlays. Only shown on the labels step where columns are being configured. */}
                  {/* [ZONE MATCHING] activeField couples the overlay to the offcanvas:
                      clicking a stripe label or an offcanvas card both set activeField,
                      which dims non-active stripes in both directions. */}
                  {activeStep === "labels" && currentPageHasTable && (
                    <ColumnZoneOverlay
                      columnZones={columnZones}
                      separatorPositions={separatorPositions}
                      containerWidth={pdfPixelWidth}
                      containerHeight={pdfPixelHeight}
                      stripeTopNorm={stripeTopNorm}
                      stripeBottomNorm={stripeBottomNorm}
                      onSeparatorMouseDown={handleSeparatorDragStart}
                      activeField={activeField}
                      onZoneClick={handleZoneClick}
                    />
                  )}
                  <PassiveOverlayBoxes
                    boxes={displayBoxes}
                    containerWidth={pdfPixelWidth}
                    containerHeight={pdfPixelHeight}
                    currentPage={pageNo}
                    onBoxClick={drawFieldId ? undefined : handleOverlayBoxClick}
                  />
                  {drawFieldId && drawPreview && (
                    <div
                      style={{
                        position: "absolute",
                        left: `${drawPreview.left}px`,
                        top: `${drawPreview.top}px`,
                        width: `${drawPreview.width}px`,
                        height: `${drawPreview.height}px`,
                        border: "1px dashed #0d6efd",
                        background: "#0d6efd22",
                        zIndex: 7,
                        pointerEvents: "none",
                        boxSizing: "border-box",
                      }}
                    />
                  )}
                </div>
              </div>
            </>
          ) : (
            <div className="flex-grow-1 d-flex align-items-center justify-content-center">
              <span className="text-muted">Loading PDF...</span>
            </div>
          )}
        </div>

        {/* Right Panel */}
        <div
          className="border-start bg-white d-flex flex-column"
          style={{
            width: rightPanelWidth,
            minWidth: 260,
            height: "100%",
            transition: isDragging ? "none" : "width 0.2s",
            overflow: "hidden",
            willChange: "width",
          }}
        >
          <div className="p-3 flex-grow-1 overflow-auto">
            {isLoadingOnboard ? (
              <span className="text-muted">Loading onboard data...</span>
            ) : (
              <>
                {activeStep === "labels" ? (
                  <>
                    <div className="mb-3">
                      <div className="fw-semibold small text-uppercase text-muted mb-2">Statement</div>
                      <div className="small mb-2" style={{ color: "#6c757d" }}>
                        Required: Supplier Parent Name or Franchise Name, and Statement Date.
                      </div>
                      <div className="small mb-2" style={{ color: "#6c757d" }}>
                        <span style={{ color: "#b02a37" }}>●</span> required, <span style={{ color: "#198754" }}>●</span> required + confirmed
                      </div>
                      <div className="small mb-2" style={{ color: statementMandatoryStatus.allRequiredSatisfied ? "#198754" : "#b02a37" }}>
                        {statementMandatoryStatus.allRequiredSatisfied
                          ? "All required statement labels are confirmed."
                          : `Missing: ${statementMandatoryStatus.missing.join(", ")}`}
                      </div>
                      <div className="d-flex flex-wrap gap-2">
                        {visibleStatementCards.length === 0
                          ? <span className="text-muted">No statement labels found on this page.</span>
                          : visibleStatementCards.map((item) => {
                              const isAnyOf = MANDATORY_STATEMENT_ANY_OF.includes(item?.label);
                              const isMandatory = isAnyOf || MANDATORY_STATEMENT_ALL.includes(item?.label);
                              const requirementSatisfied = isAnyOf ? statementMandatoryStatus.parentOrTradingSatisfied : Boolean(statementMandatoryStatus.byLabel[item?.label]);
                              return renderFieldCard(item, HIGHLIGHT_COLORS.supplier, { isMandatory, requirementSatisfied });
                            })}
                      </div>
                    </div>

                    <div className="mb-3">
                      <div className="fw-semibold small text-uppercase text-muted mb-2">Invoices</div>
                      <div className="small mb-2" style={{ color: "#6c757d" }}>
                        Required: Invoice Date, Invoice ID, and Invoice Amount columns.
                      </div>
                      <div className="small mb-2" style={{ color: "#6c757d" }}>
                        <span style={{ color: "#b02a37" }}>●</span> required, <span style={{ color: "#198754" }}>●</span> required + confirmed
                      </div>
                      <div className="small mb-2" style={{ color: invoiceMandatoryStatus.allRequiredSatisfied ? "#198754" : "#b02a37" }}>
                        {invoiceMandatoryStatus.allRequiredSatisfied
                          ? "All required invoice columns are confirmed."
                          : `Missing: ${invoiceMandatoryStatus.missing.join(", ")}`}
                      </div>
                      <div className="d-flex flex-wrap gap-2">
                        {visibleInvoiceMappings.length === 0
                          ? <span className="text-muted">No invoice columns found on this page.</span>
                          : visibleInvoiceMappings.map((item) => {
                              const isMandatory = MANDATORY_INVOICE_COLUMNS.includes(item?.labelType);
                              const requirementSatisfied = Boolean(invoiceMandatoryStatus.byType[item?.labelType]);
                              // [ZONE MATCHING] Pass zone color as the active-icon color so hovering/clicking
                          // makes icons turn the same color as the column's PDF stripe
                          return renderFieldCard(item, ZONE_COLORS[item.labelType] || HIGHLIGHT_COLORS.invoiceHeaderActive, { isMandatory, requirementSatisfied });
                            })}
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="mb-2">
                    <div className="fw-semibold small text-uppercase text-muted mb-2">Confirm supplier names</div>
                    <div className="small text-muted mb-3">
                      Confirm who this statement gets paid to. Pick the payee from your existing suppliers — new suppliers arrive via the ERP sync.
                    </div>

                    {payeeGate.status === "checking" && (
                      <div className="border rounded p-3 mb-2 small text-secondary">Checking known supplier names…</div>
                    )}

                    {payeeGate.status === "alias_hit" && payeeGate.hit && (
                      <div className="border rounded p-3 mb-2" style={{ background: "#f6fff8", borderColor: "#bcdcc8" }}>
                        <div className="small text-secondary">Payee (resolved from a known name)</div>
                        <div className="fw-semibold fs-6 mt-1">{payeeGate.hit.master_name}</div>
                        <div className="text-secondary" style={{ fontSize: "0.78rem" }}>
                          “{payeeGate.hit.alias_name_raw || payeeGate.scanned}” is a confirmed name for this supplier — no re-confirmation needed.
                        </div>
                        <button type="button" className="btn btn-link btn-sm px-0 mt-1"
                                onClick={() => setShowPayeePicker(true)}>
                          Change payee…
                        </button>
                      </div>
                    )}

                    {(payeeGate.status === "needs_pick" || payeeGate.status === "no_name") && (
                      <div className="border rounded p-3 mb-2">
                        <div className="small text-secondary">Payee</div>
                        {supplierSelectedRecords?.[payeeGate.slot || "trading"] ? (
                          <div className="fw-semibold fs-6 mt-1">
                            {getSupplierRecordName(supplierSelectedRecords[payeeGate.slot || "trading"])}
                          </div>
                        ) : (
                          <div className="text-secondary small mt-1">
                            “{payeeGate.scanned || "(no supplier name detected)"}” is not a known supplier name yet.
                          </div>
                        )}
                        <button type="button" className="btn btn-outline-primary btn-sm mt-2"
                                onClick={() => setShowPayeePicker(true)}
                                disabled={payeeGate.status === "no_name" && !payeeGate.scanned}>
                          {supplierSelectedRecords?.[payeeGate.slot || "trading"] ? "Change selection…" : "Select payee…"}
                        </button>
                        {payeeGate.status === "no_name" && (
                          <div className="small text-secondary mt-2">
                            Supplier not yet imported from Protractor. Try again after the next ERP sync (runs every 2 hours).
                          </div>
                        )}
                      </div>
                    )}

                    {mergeCandidates.length > 0 && (
                      <div className="border rounded p-3 mb-2" style={{ background: "#fffdf0", borderColor: "#e0c200" }}>
                        <div className="fw-semibold small">Are these the same supplier?</div>
                        <div className="small text-secondary mb-1">
                          Checked suppliers will be merged into the payee when you publish. Skipping just binds this statement.
                        </div>
                        {mergeCandidates.map((c) => (
                          <label key={`merge-${c.id}`} className="d-block small">
                            <input
                              type="checkbox"
                              checked={mergeRequests.includes(c.id)}
                              onChange={(e) => setMergeRequests((prev) =>
                                e.target.checked ? [...prev, c.id] : prev.filter((x) => x !== c.id))}
                            />{" "}
                            Merge “{c.name}” into the selected payee
                          </label>
                        ))}
                      </div>
                    )}

                  </div>
                )}
              </>
            )}
          </div>

          <div className="border-top p-3 d-flex justify-content-between align-items-center gap-2 flex-wrap">
            <div className="small">
              {saveFeedback?.message && (
                <div className={saveFeedback?.variant === "danger" ? "text-danger" : saveFeedback?.variant === "warning" ? "text-warning" : "text-success"}>
                  {saveFeedback.message}
                </div>
              )}
              {publishResult?.published && (
                <div className="text-success mt-1">
                  Published{publishResult?.config_id != null ? ` • Config #${publishResult.config_id}` : ""}
                  {publishResult?.config_profile_id != null ? ` • Profile #${publishResult.config_profile_id}` : ""}
                </div>
              )}
              {!publishResult?.published && effectivePublishMissing.length > 0 && (
                <div className="text-warning mt-1">
                  Publish requirements missing: {effectivePublishMissing.join(", ")}
                </div>
              )}
            </div>
            <div className="d-flex justify-content-end gap-2">
            {activeStep === "supplier" && (
              <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setActiveStep("labels")}>
                Back
              </button>
            )}
            {activeStep === "supplier" && (
              <button
                type="button"
                className="btn btn-outline-primary btn-sm ms-2 d-inline-flex align-items-center gap-2"
                onClick={handleSaveSupplierStep}
                disabled={isSavingDraft}
              >
                {isSavingDraft && (
                  <span
                    className="spinner-border spinner-border-sm"
                    role="status"
                    aria-hidden="true"
                  />
                )}
                {isSavingDraft ? "Saving…" : "Save Draft"}
              </button>
            )}
            {activeStep === "labels" && (
              <button
                type="button"
                className="btn btn-primary btn-sm d-inline-flex align-items-center gap-2"
                onClick={handleNextStep}
                disabled={!allRequiredLabelsConfirmed || isSavingDraft}
              >
                {isSavingDraft && (
                  <span
                    className="spinner-border spinner-border-sm"
                    role="status"
                    aria-hidden="true"
                  />
                )}
                {isSavingDraft ? "Saving…" : "Next"}
              </button>
            )}
            {activeStep === "supplier" && (
              <button
                type="button"
                className="btn btn-primary btn-sm ms-5 d-inline-flex align-items-center gap-2"
                onClick={handlePublish}
                disabled={!effectiveCanPublish || isSavingDraft || isPublishing || publishResult?.published}
              >
                {isPublishing && (
                  <span
                    className="spinner-border spinner-border-sm"
                    role="status"
                    aria-hidden="true"
                  />
                )}
                {publishResult?.published ? "Published" : isPublishing ? "Publishing…" : "Publish"}
              </button>
            )}
            </div>
          </div>
        </div>

        <PayeePickerModal
          show={showPayeePicker}
          zIndex={2060}
          onClose={() => setShowPayeePicker(false)}
          onSelect={(record, allResults) => {
            if (payeeGate.status === "alias_hit") {
              setShowPayeePicker(false);
              setPendingRelink(record);
            } else {
              handlePayeePicked(record, allResults);
            }
          }}
          title={payeeGate.status === "alias_hit" ? "Change payee for this supplier name" : "Select payee"}
          initialQuery={payeeGate.scanned}
          excludeMasterId={payeeGate.hit?.master_id ?? null}
        />
        {pendingRelink && (
          <div className="modal d-block" tabIndex={-1} style={{ background: "rgba(0,0,0,0.4)", zIndex: 2070 }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-body">
                  <p className="fw-semibold mb-1">Change payee?</p>
                  <p className="small text-secondary mb-0">
                    Future statements named “{payeeGate.hit?.alias_name_raw || payeeGate.scanned}” will resolve to {pendingRelink?.name}. This name's ERP history moves with it.
                  </p>
                </div>
                <div className="modal-footer py-2">
                  <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setPendingRelink(null)}>Cancel</button>
                  <button type="button" className="btn btn-primary btn-sm" onClick={handleConfirmRelink}>Change payee</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Draggable Grip */}
        <div
          className="draggable-grip"
          style={{
            position: "absolute",
            top: "50%",
            left: `calc(100% - ${rightPanelWidth}px - 13px)`,
            transform: "translateY(-50%)",
            cursor: "ew-resize",
            zIndex: 1055,
          }}
          onMouseDown={handleMouseDown}
        >
          <FaGripLinesVertical size={24} />
        </div>
      </div>

    </Modal>
  );
}
